import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Typography,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  TextField,
  Chip,
  CircularProgress
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import CropIcon from '@mui/icons-material/Crop';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import { v4 as uuidv4 } from 'uuid';
import { chartDataService } from '../services/chartDataService';
import { youtubeService } from '../services/youtubeService';
import { SERVER_BASE_URL } from '../config';
import { useThemeColors } from '../theme/useThemeColors';
import { authedEventSource } from '../lib/authedEventSource';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface VideoMetadata {
  width: number;
  height: number;
  duration: number;
  aspectRatio: number;
}

export const UploadVideo = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { themeColor } = useThemeColors();

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Player dimensions state
  const [playerRect, setPlayerRect] = useState<{ width: number; height: number } | null>(null);

  // Upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Video states
  const [videoUrl, setVideoUrl] = useState<string>('');
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState<boolean>(false);

  // Crop states
  const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
  const [dragCorner, setDragCorner] = useState<'tl' | 'tr' | 'bl' | 'br' | 'move' | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number; cropArea: CropArea } | null>(null);
  const [isAIDetecting, setIsAIDetecting] = useState(false);
  const [showCropOverlay, setShowCropOverlay] = useState<boolean>(false);

  // Processing states
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  // Mirror isProcessing into a ref so deferred callbacks (e.g. the 90s safety-net
  // timers) read the CURRENT value instead of a stale closure captured at setup.
  const isProcessingRef = useRef<boolean>(false);
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  const [processingStep, setProcessingStep] = useState<string>('');
  const [processingPhase, setProcessingPhase] = useState<'horizontal' | 'vertical' | null>(null);
  
  // Vertical progress simulation
  const [verticalProgressSimulation, setVerticalProgressSimulation] = useState<number>(0);
  const verticalProgressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Add state for videoId
  const [videoId, setVideoId] = useState<string | null>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  // Helper function to start vertical progress simulation
  // Sieve takes ~2 minutes per 1 minute of video
  const startVerticalProgressSimulation = (videoDuration?: number, updateProgressDirectly = false) => {
    if (verticalProgressIntervalRef.current) return; // Already running

    const duration = videoDuration || 180; // Default 3 min if not provided
    const estimatedProcessingTime = duration * 2 * 1000; // in ms
    const intervalMs = Math.max(1000, estimatedProcessingTime / 95); // At least 1 second between updates

    console.log(`[SSE Debug] Starting vertical progress simulation (video: ${duration}s, interval: ${Math.round(intervalMs)}ms)`);
    setVerticalProgressSimulation(0);

    verticalProgressIntervalRef.current = setInterval(() => {
      setVerticalProgressSimulation(prev => {
        const nextProgress = Math.min(prev + 1, 95); // Cap at 95% until actual completion
        console.log(`[SSE Debug] Vertical progress simulation: ${nextProgress}%`);
        if (updateProgressDirectly) {
          setProcessingProgress(nextProgress);
        }
        return nextProgress;
      });
    }, intervalMs);
  };

  // Add state for 4:3 video detection and blurred preview
  const [is43Video, setIs43Video] = useState<boolean>(false);
  const [blurredPreviewUrl, setBlurredPreviewUrl] = useState<string | null>(null);
  const [isPillarboxed43, setIsPillarboxed43] = useState<boolean>(false);
  const [isLetterboxed, setIsLetterboxed] = useState<boolean>(false);
  const [isPillarboxed, setIsPillarboxed] = useState<boolean>(false);
  const [isWideFile, setIsWideFile] = useState<boolean>(false);

  // Add state for tracking video results
  const [processedVideoUrl, setProcessedVideoUrl] = useState<string | null>(null);
  const [verticalVideoUrl, setVerticalVideoUrl] = useState<string | null>(null);
  const [showRetryVertical, setShowRetryVertical] = useState<boolean>(false);

  // Add state for YouTube download functionality
  const [youtubeUrl, setYoutubeUrl] = useState<string>('');
  const [isDownloadingFromYoutube, setIsDownloadingFromYoutube] = useState<boolean>(false);
  const [isInitializingDownload, setIsInitializingDownload] = useState<boolean>(false); // Shows spinner while fetching metadata
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [downloadStep, setDownloadStep] = useState<string>('');
  const [downloadFailed, setDownloadFailed] = useState<boolean>(false); // Track if download failed for retry button

  // States for YouTube downloaded video path and source tracking
  const [serverVideoPath, setServerVideoPath] = useState<string | null>(null); // Server-relative path for both YouTube downloads and local uploads
  const [youtubeOriginalFileName, setYoutubeOriginalFileName] = useState<string | null>(null);
  const [isVideoFromYoutubeSource, setIsVideoFromYoutubeSource] = useState<boolean>(false);

  // Effect to handle youtubeUrl query parameter
  const location = useLocation();

  const [isDragging, setIsDragging] = useState(false);
  const [videoManagerRowIndex, setVideoManagerRowIndex] = useState<number | null>(null);
  const [songFromVideoManager, setSongFromVideoManager] = useState<string | null>(null);
  const [artistFromVideoManager, setArtistFromVideoManager] = useState<string | null>(null);
  const [returnUrlFromTease, setReturnUrlFromTease] = useState<string | null>(null);
  const [returnSlotFromTease, setReturnSlotFromTease] = useState<string | null>(null);

  const processedQueryUrlRef = useRef<string | null>(null); // Ref to track processed YouTube URL from query
  const processedJobIdRef = useRef<string | null>(null); // Ref to track which jobId we've processed
  const initialYoutubeUrlRef = useRef<string | null>(null); // Ref to track the first YouTube URL loaded from query params for this instance
  const completionPollRef = useRef<NodeJS.Timeout | null>(null); // Ref for completion polling
  const downloadPollRef = useRef<NodeJS.Timeout | null>(null); // Ref for download completion polling
  const processedQueryParamsRef = useRef<boolean>(false); // Ref to prevent multiple query param processing
  const activeDownloadRef = useRef<string | null>(null); // Ref to prevent duplicate downloads of same URL
  const horizontalUrlWrittenRef = useRef<boolean>(false); // Ref to track if horizontal URL has been written to sheet
  const verticalPollRef = useRef<NodeJS.Timeout | null>(null); // Ref for vertical completion polling
  const verticalPollActiveRef = useRef<boolean>(false); // Prevent duplicate polling
  const currentVideoIdRef = useRef<string | null>(null); // Ref to track current video ID and ignore events from old downloads

  // Determine if this is "manual upload" mode (no job or video specified via URL params)
  // Only show file select and YouTube URL fields when accessed directly (e.g., from Video Manager's "Upload Video" button)
  const isManualUploadMode = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const hasJobId = !!params.get('jobId');
    const hasYoutubeUrl = !!params.get('youtubeUrl');
    const hasLocalVideoPath = !!params.get('localVideoPath');
    return !hasJobId && !hasYoutubeUrl && !hasLocalVideoPath;
  }, [location.search]);

  // Helper function to navigate after completion - either to return URL or video manager
  const navigateAfterCompletion = useCallback((verticalS3Url?: string, delay: number = 2000) => {
    setTimeout(() => {
      // Check if we should return to Tease page
      if (returnUrlFromTease && returnSlotFromTease && verticalS3Url) {
        console.log('[UploadVideo] Navigating back to Tease page with video URL');
        const returnWithParams = `${returnUrlFromTease}${returnUrlFromTease.includes('?') ? '&' : '?'}prefillSlot=${returnSlotFromTease}&prefillUrl=${encodeURIComponent(verticalS3Url)}`;
        navigate(returnWithParams);
      } else {
        // Default: navigate to video manager
        navigate('/video-manager', {
          state: videoManagerRowIndex !== null ? { highlightRow: videoManagerRowIndex } : undefined
        });
      }
    }, delay);
  }, [navigate, returnUrlFromTease, returnSlotFromTease, videoManagerRowIndex]);

  // Function to update Master Video List with S3 URLs
  const updateMasterVideoListUrls = useCallback(async (horizontalUrl: string, verticalUrl?: string, extractedMetadata?: { song?: string; artist?: string }, shouldNavigate: boolean = true) => {
    // Extract fresh data from URL parameters instead of relying on potentially reset state
    const params = new URLSearchParams(location.search);
    const rowIndexFromUrl = params.get('videoManagerRowIndex');
    const songFromUrl = params.get('song');
    const artistFromUrl = params.get('artist');
    
    // Use fresh URL parameters instead of potentially reset state, or extracted metadata as fallback
    const initialRowIndex = rowIndexFromUrl ? parseInt(rowIndexFromUrl, 10) : (videoManagerRowIndex ?? null);
    const finalSong = songFromUrl || songFromVideoManager || extractedMetadata?.song || null;
    const finalArtist = artistFromUrl || artistFromVideoManager || extractedMetadata?.artist || null;
    
    // Row index (from URL param or state) is used only for the VideoManager
    // highlight on navigate; the DB resolves the actual row by song+artist.
    const highlightRowIndex = initialRowIndex;
    let searchSong = finalSong;
    let searchArtist = finalArtist;

    // If we don't have song/artist but have a YouTube URL, try to extract from YouTube API
    if ((!searchSong || !searchArtist) && youtubeUrl) {
      console.log('[Video Update] No song/artist provided, extracting from YouTube URL:', youtubeUrl);
      try {
        const songInfo = await youtubeService.getSongInfo(youtubeUrl);
        searchSong = searchSong || songInfo.song;
        searchArtist = searchArtist || songInfo.artist;
        console.log(`[Video Update] Extracted from YouTube API: "${searchArtist}" - "${searchSong}"`);
      } catch (error) {
        console.error('[Video Update] Failed to extract song info from YouTube:', error);
      }
    }

    // Only update if we have a (song, artist) key — the DB resolves the row by it.
    if (!searchSong || !searchArtist) {
      console.log('[Video Update] Skipping update - missing song/artist:', {
        searchSong,
        searchArtist,
        finalSong,
        finalArtist,
        rowIndexFromUrl,
        songFromUrl,
        artistFromUrl,
        videoManagerRowIndex,
        songFromVideoManager,
        artistFromVideoManager
      });
      console.log('[Video Update] Not updating Master Video List - missing metadata');

      // Still navigate to VideoManager even without updating (only if shouldNavigate is true)
      if (shouldNavigate) {
        enqueueSnackbar('✅ Video processing complete! Navigate to Video Manager to view your video.', { variant: 'success' });
        setTimeout(() => {
          console.log('[Video Update] Navigating to VideoManager (without update)');
          navigate('/video-manager', {
            state: videoManagerRowIndex !== null ? { highlightRow: videoManagerRowIndex } : undefined
          });
        }, 2500);
      }
      return;
    }

    // Skip update if no URLs provided
    if (!horizontalUrl && !verticalUrl) {
      console.log('[Video Update] No URLs to update, skipping update');
      return;
    }

    try {
      console.log(`[Video Update] Updating Master Video List for ${searchArtist} - ${searchSong}`);

      // Upsert/update the matching video by (song, artist) — only the URLs provided.
      const fields: { s3Url?: string; verticalUrl?: string } = {};
      if (horizontalUrl) {
        fields.s3Url = horizontalUrl;
        console.log(`[Video Update] Setting horizontal URL: ${horizontalUrl}`);
      }
      if (verticalUrl) {
        fields.verticalUrl = verticalUrl;
        console.log(`[Video Update] Setting vertical URL: ${verticalUrl}`);
      }

      await chartDataService.updateVideoByKey(searchSong, searchArtist, fields);

      console.log('[Video Update] Successfully updated Master Video List');

      // Show appropriate message based on whether we're updating one or both URLs
      const urlMessage = horizontalUrl && verticalUrl
        ? '✅ Master Video List updated with S3 URLs'
        : '✅ Master Video List updated with horizontal S3 URL';
      enqueueSnackbar(urlMessage, { variant: 'success' });

      // Navigate back to VideoManager after successful update with highlight (only if shouldNavigate is true)
      if (shouldNavigate) {
        setTimeout(() => {
          console.log('[Video Update] Navigating back to VideoManager with highlight for row:', highlightRowIndex);
          navigate('/video-manager', {
            state: highlightRowIndex !== null ? { highlightRow: highlightRowIndex } : undefined
          });
        }, 2500); // Give user time to see the success message
      }
      
    } catch (error) {
      console.error('[Video Update] Error updating Master Video List:', error);
      enqueueSnackbar(`⚠️ Failed to update Master Video List: ${error instanceof Error ? error.message : 'Unknown error'}`, { 
        variant: 'warning' 
      });
    }
  }, [enqueueSnackbar, navigate, videoManagerRowIndex, songFromVideoManager, artistFromVideoManager]);

  // Function to poll import job for vertical completion
  const startVerticalCompletionPolling = useCallback(() => {
    // Don't start if already polling
    if (verticalPollActiveRef.current) {
      console.log('[Vertical Poll] Already polling, skipping');
      return;
    }

    const jobId = processedJobIdRef.current;
    if (!jobId) {
      console.log('[Vertical Poll] No jobId available, cannot poll');
      return;
    }

    console.log('[Vertical Poll] Starting vertical completion polling for jobId:', jobId);
    verticalPollActiveRef.current = true;

    const pollJobStatus = () => {
      fetch(`${SERVER_BASE_URL}/api/import-manager/jobs/${jobId}`)
        .then(res => res.json())
        .then(data => {
          if (data.success && data.job) {
            console.log('[Vertical Poll] Job status:', data.job.status);
            if (data.job.status === 'completed') {
              console.log('[Vertical Poll] Vertical generation complete!');
              verticalPollActiveRef.current = false;
              setIsProcessing(false);
              setProcessingProgress(100);
              enqueueSnackbar('Video processing complete!', { variant: 'success' });

              // Navigate after completion - either to return URL or video manager
              navigateAfterCompletion(data.job.vertical_s3_url);
            } else if (data.job.status === 'failed') {
              console.log('[Vertical Poll] Vertical generation failed:', data.job.error_message);
              verticalPollActiveRef.current = false;
              setIsProcessing(false);
              enqueueSnackbar(`Vertical video generation failed: ${data.job.error_message || 'Unknown error'}`, { variant: 'error' });
            } else if (data.job.status === 'generating_vertical' || data.job.status === 'processing_v') {
              // Still processing, poll again in 5 seconds
              verticalPollRef.current = setTimeout(pollJobStatus, 5000);
            } else {
              // Unexpected status, keep polling
              console.log('[Vertical Poll] Unexpected status:', data.job.status);
              verticalPollRef.current = setTimeout(pollJobStatus, 5000);
            }
          }
        })
        .catch(err => {
          console.error('[Vertical Poll] Error:', err);
          // Retry on error
          verticalPollRef.current = setTimeout(pollJobStatus, 5000);
        });
    };

    // Start polling after 2 seconds
    verticalPollRef.current = setTimeout(pollJobStatus, 2000);
  }, [enqueueSnackbar, navigate, videoManagerRowIndex]);

  // Function to handle vertical-only generation (when horizontal already exists)
  const startVerticalOnlyGeneration = useCallback(async (
    horizontalUrl: string,
    newVideoId: string,
    song: string | null,
    artist: string | null,
    rowIndex: string | null
  ) => {
    try {
      // Create import job
      let importJobId: string | null = null;
      try {
        const importJobResponse = await fetch(`${SERVER_BASE_URL}/api/import-manager/jobs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            song: song || 'Unknown',
            artist: artist || 'Unknown',
            status: 'generating_vertical',
            horizontal_s3_url: horizontalUrl,
            video_id: newVideoId,
            sheet_row_index: rowIndex ? parseInt(rowIndex, 10) : null
          })
        });
        const importJobData = await importJobResponse.json();
        if (importJobData.success && importJobData.jobs?.[0]?.id) {
          importJobId = importJobData.jobs[0].id.toString();
          processedJobIdRef.current = importJobId;
          console.log('[UploadVideo] Created import job for vertical generation:', importJobId);

          // Update URL to use jobId instead of verticalMode params
          // This enables proper state restoration if user navigates away and back
          navigate(`/upload-video?jobId=${importJobId}`, { replace: true });
        }
      } catch (importErr) {
        console.warn('[UploadVideo] Failed to create import job:', importErr);
      }

      // Call vertical generation endpoint
      const originalFilename = artist && song ? `${artist} - ${song}.mp4` : `video_${Date.now()}.mp4`;

      const response = await fetch(`${SERVER_BASE_URL}/api/generate-vertical`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processedVideoUrl: horizontalUrl,
          originalFilename,
          videoId: newVideoId,
          importJobId
        })
      });

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to start vertical generation');
      }

      console.log('[UploadVideo] Vertical generation started, beginning polling');
      // Start polling for completion (same as existing vertical polling)
      startVerticalCompletionPolling();

    } catch (error) {
      console.error('[UploadVideo] Error starting vertical generation:', error);
      enqueueSnackbar(`Failed to start vertical generation: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
      setIsProcessing(false);
    }
  }, [enqueueSnackbar, navigate, startVerticalCompletionPolling]);

  // Function to poll for completion when SSE drops
  const pollForCompletion = useCallback(async (videoId: string) => {
    console.log(`[Completion Poll] Starting polling for videoId: ${videoId}`);
    
    let pollCount = 0;
    const maxPolls = 180; // 15 minutes max (vertical processing can take 5-10 minutes)
    
    const checkCompletion = async () => {
      try {
        pollCount++;
        const response = await fetch(`${SERVER_BASE_URL}/api/video-completion/${videoId}`);
        const data = await response.json();
        
        console.log(`[Completion Poll] Poll ${pollCount}/${maxPolls}, response:`);
        console.log('  completed:', data.completed);
        console.log('  s3Url:', data.s3Url);
        console.log('  verticalUrl:', data.verticalUrl);

        // Write horizontal URL as soon as it's available (only once)
        if (data.s3Url && !processedVideoUrl && !horizontalUrlWrittenRef.current) {
          console.log(`[Completion Poll] Horizontal URL available, updating sheet now`);
          horizontalUrlWrittenRef.current = true;
          setProcessedVideoUrl(data.s3Url);

          // Update sheet but DON'T navigate yet (vertical still processing)
          updateMasterVideoListUrls(data.s3Url, data.verticalUrl, undefined, false);
        }

        // Complete when both videos are ready
        if (data.completed && data.s3Url && data.verticalUrl) {
          console.log(`[Completion Poll] Found completion data!`, data);

          // Clear polling
          if (completionPollRef.current) {
            clearInterval(completionPollRef.current);
            completionPollRef.current = null;
          }

          // Update state
          setProcessedVideoUrl(data.s3Url);
          setVerticalVideoUrl(data.verticalUrl);
          setProcessingProgress(100);
          setProcessingStep('Processing complete! Both videos ready.');
          enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });

          // Update Master Video List with both URLs and NOW navigate
          updateMasterVideoListUrls(data.s3Url, data.verticalUrl, undefined, true);

          setIsProcessing(false);
          setProcessingPhase(null);
        } else if (data.completed && data.s3Url && !data.verticalUrl) {
          // Horizontal is done, write to sheet immediately (vertical might still be processing or failed)
          console.log(`[Completion Poll] Horizontal complete, writing to sheet`);

          // Write horizontal URL to sheet immediately
          if (!processedVideoUrl) {
            setProcessedVideoUrl(data.s3Url);
            updateMasterVideoListUrls(data.s3Url, undefined, data.extractedMetadata);
          }

          // Continue waiting for vertical
          setProcessingStep('🎬 Creating Vertical Output ⚡');
          setProcessingProgress(85);
        } else if (pollCount >= maxPolls) {
          console.log(`[Completion Poll] Max polls reached without completion`);

          // Make sure horizontal URL is written to sheet even if vertical timed out
          if (processedVideoUrl && !verticalVideoUrl) {
            console.log(`[Completion Poll] Timeout - ensuring horizontal URL is written to sheet`);
            updateMasterVideoListUrls(processedVideoUrl, undefined);
          }

          // Clear polling
          if (completionPollRef.current) {
            clearInterval(completionPollRef.current);
            completionPollRef.current = null;
          }
          setProcessingStep('Processing timeout - please check VideoManager');
          enqueueSnackbar('Processing may have completed. Please check VideoManager.', { variant: 'warning' });
          setIsProcessing(false);
        }
      } catch (error) {
        console.error('[Completion Poll] Error checking completion:', error);
      }
    };
    
    // Start polling every 5 seconds
    completionPollRef.current = setInterval(checkCompletion, 5000);
    
    // Also check immediately
    checkCompletion();
  }, [enqueueSnackbar, updateMasterVideoListUrls]);

  // Function to poll for download completion when SSE drops during YouTube download
  // This handles the case where the webhook-based download completes but SSE gets ECONNRESET
  const pollForDownloadCompletion = useCallback(async (jobId: string) => {
    console.log(`[Download Poll] Starting polling for jobId: ${jobId}`);

    let pollCount = 0;
    const maxPolls = 60; // 5 minutes max (download should be done by now)

    const checkDownloadCompletion = async () => {
      try {
        pollCount++;
        console.log(`[Download Poll] Poll ${pollCount}/${maxPolls} for job ${jobId}`);

        const apiUrl = process.env.NODE_ENV === 'development'
          ? `${SERVER_BASE_URL}/api/import-manager/jobs/${jobId}`
          : `/api/import-manager/jobs/${jobId}`;

        const response = await fetch(apiUrl);
        const data = await response.json();

        console.log(`[Download Poll] Job status: ${data.job?.status}, has S3 URL: ${!!data.job?.youtube_download_s3_url}`);

        if (data.success && data.job) {
          const job = data.job;

          // Check if download is complete (status is crop_needed, completed, or processing/generating)
          if (job.status === 'crop_needed' || job.status === 'completed' ||
              job.status === 'generating_horizontal' || job.status === 'generating_vertical' ||
              job.status === 'processing_h' || job.status === 'processing_v') {
            // Prefer local path over S3 URL to avoid CORS issues during crop preview
            const videoUrl = (job.youtube_download_path ? `${SERVER_BASE_URL}${job.youtube_download_path.replace('/app/server', '')}` : null) ||
              job.youtube_download_s3_url;

            if (videoUrl) {
              console.log(`[Download Poll] Download complete! Loading video for crop: ${videoUrl}`);

              // Clear polling
              if (downloadPollRef.current) {
                clearInterval(downloadPollRef.current);
                downloadPollRef.current = null;
              }

              // Stop showing download progress
              setIsDownloadingFromYoutube(false);
              setDownloadProgress(100);
              setDownloadStep('Download complete!');

              // Load the video for cropping
              setVideoUrl(videoUrl);
              // Set serverVideoPath so processVideo() knows the video source
              const serverPath = job.youtube_download_path || job.youtube_download_s3_url;
              setServerVideoPath(serverPath);
              setIsVideoFromYoutubeSource(true);
              enqueueSnackbar('Video downloaded! Select crop area.', { variant: 'success' });
              return;
            }
          } else if (job.status === 'failed') {
            console.error(`[Download Poll] Job failed: ${job.error_message}`);

            if (downloadPollRef.current) {
              clearInterval(downloadPollRef.current);
              downloadPollRef.current = null;
            }

            setIsDownloadingFromYoutube(false);
            setDownloadFailed(true);
            setDownloadStep('Download failed');
            enqueueSnackbar(`Download failed: ${job.error_message || 'Unknown error'}`, { variant: 'error' });
            return;
          }

          // Still downloading - update UI
          if (job.status === 'downloading') {
            setDownloadStep('Downloading from YouTube...');
          }
        }

        if (pollCount >= maxPolls) {
          console.log(`[Download Poll] Max polls reached without completion`);

          if (downloadPollRef.current) {
            clearInterval(downloadPollRef.current);
            downloadPollRef.current = null;
          }

          setIsDownloadingFromYoutube(false);
          enqueueSnackbar('Download status unknown. Please check Import Manager.', { variant: 'warning' });
        }
      } catch (error) {
        console.error('[Download Poll] Error checking download status:', error);
      }
    };

    // Start polling every 5 seconds
    downloadPollRef.current = setInterval(checkDownloadCompletion, 5000);

    // Also check immediately
    checkDownloadCompletion();
  }, [enqueueSnackbar]);

  const resetForm = () => {
    setSelectedFile(null);
    setVideoUrl('');
    setVideoMetadata(null);
    setIsVideoLoaded(false);
    setCropArea({ x: 0, y: 0, width: 0, height: 0 });
    setShowCropOverlay(false);
    setIsProcessing(false);
    setProcessingProgress(0);
    setProcessingStep('');
    setProcessingPhase(null);
    
    // Clean up vertical progress simulation
    if (verticalProgressIntervalRef.current) {
      clearInterval(verticalProgressIntervalRef.current);
      verticalProgressIntervalRef.current = null;
    }
    setVerticalProgressSimulation(0);
    setUploadError(null);
    setVideoId(null);
    
    // Clean up video result state
    setProcessedVideoUrl(null);
    setVerticalVideoUrl(null);
    setShowRetryVertical(false);
    
    // Clean up 4:3 video state and blurred preview
    setIs43Video(false);
    setIsPillarboxed43(false);
    setIsLetterboxed(false);
    setIsPillarboxed(false);
    setIsWideFile(false);
    if (blurredPreviewUrl) {
      URL.revokeObjectURL(blurredPreviewUrl);
      setBlurredPreviewUrl(null);
    }
    
    // Clean up polling intervals
    if (completionPollRef.current) {
      clearInterval(completionPollRef.current);
      completionPollRef.current = null;
    }
    if (downloadPollRef.current) {
      clearInterval(downloadPollRef.current);
      downloadPollRef.current = null;
    }

    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Function to start normalization with a local file (skips YouTube download)
  const startNormalizationWithLocalFile = useCallback(async (localVideoPath: string) => {
    console.log(`[LocalFile] Starting normalization process with local file: ${localVideoPath}`);

    const currentExecutionVideoId = uuidv4(); // Generate videoId for this specific execution
    console.log(`[LocalFile] Using videoId: ${currentExecutionVideoId}`);

    setVideoId(currentExecutionVideoId);
    setSelectedFile(null);
    setVideoUrl('');
    setIsVideoFromYoutubeSource(false);

    // Store the local video path so AI Detect Crop can use it
    setServerVideoPath(localVideoPath);

    // Close any previous EventSource instances
    if (eventSource) {
      console.log('[LocalFile] Closing existing eventSource before processing.');
      eventSource.close();
      setEventSource(null);
    }
    if (eventSourceRef.current) {
      console.log('[LocalFile] Closing existing eventSourceRef.current.');
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    // Set the video URL to the local file path
    const serverBaseUrl = process.env.NODE_ENV === 'development' ? SERVER_BASE_URL : '';
    const fullVideoPathOnServer = `${serverBaseUrl}${localVideoPath}`;

    console.log('[LocalFile] Setting video URL to:', fullVideoPathOnServer);
    setVideoUrl(fullVideoPathOnServer);
    videoRef.current?.load();

    // Set up SSE connection for video processing progress
    console.log('[LocalFile] Setting up processing SSE for videoId:', currentExecutionVideoId);
    const processingSseUrl = `${SERVER_BASE_URL}/api/video-progress/${encodeURIComponent(currentExecutionVideoId)}`;

    console.log('[SSE Debug] Processing SSE URL:', processingSseUrl);

    const processingSSE = await authedEventSource(processingSseUrl);
    setEventSource(processingSSE);
    eventSourceRef.current = processingSSE;

    // Safety net: Start polling after 90s if still processing (in case SSE drops silently)
    const safetyNetTimer = setTimeout(() => {
      if (isProcessingRef.current && currentExecutionVideoId) {
        console.log('[Safety Net] 90s elapsed, starting polling as backup (local file flow)');
        pollForCompletion(currentExecutionVideoId);
      }
    }, 90000);

    // Clear safety net timer on disconnect
    processingSSE.addEventListener('error', () => {
      clearTimeout(safetyNetTimer);
    });

    processingSSE.onmessage = (event) => {
      console.log('[SSE Debug] Processing Event (from local file flow):', event.data);
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE Debug] Parsed data:', data);
        console.log('[SSE Debug] Current processing phase:', processingPhase);

        // Handle FINAL completion first (phase='completed' with verticalUrl)
        if ((data.phase === 'completed' || data.phase === 'complete') && data.completed === true && data.verticalUrl) {
          console.log('[SSE Debug] FINAL completion from local file flow - vertical video ready:', data.verticalUrl);

          // Stop any polling
          if (verticalPollRef.current) {
            clearTimeout(verticalPollRef.current);
            verticalPollRef.current = null;
          }
          verticalPollActiveRef.current = false;

          setProcessingProgress(100);
          setProcessingStep('Complete!');
          setIsProcessing(false);
          setVerticalVideoUrl(data.verticalUrl);

          enqueueSnackbar('Video processing complete!', { variant: 'success' });

          // Close SSE connection to prevent reconnect loop
          processingSSE.close();
          if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
          setEventSource(prev => prev === processingSSE ? null : prev);

          // Navigate after completion - either to return URL or video manager
          navigateAfterCompletion(data.verticalUrl);
          return;
        }

        // Check for horizontal completion - switch to vertical indeterminate progress
        if (data.phase === 'horizontal_complete' ||
            data.phase === 'vertical_generation' || data.phase === 'vertical_processing' ||
            data.phase === 'vertical_polling' || data.phase === 'vertical_download' ||
            data.phase === 'video_manager_update') {
          console.log('[SSE Debug] Horizontal complete, switching to vertical indeterminate progress');
          setProcessingPhase('vertical');
          setProcessingProgress(-1); // Indeterminate progress bar
          setProcessingStep(data.message || 'Generating vertical video...');
          // For direct uploads (no jobId), we rely on SSE for completion, not polling
        } else if (data.percent !== undefined && data.phase !== 'completed') {
          // Horizontal phase - show determinate progress (but not if phase is 'completed')
          setProcessingPhase('horizontal');
          const newProgress = Math.min(100, Math.round(data.percent));
          console.log(`[SSE Debug] Horizontal progress: ${data.percent}% → Display: ${newProgress}%`);
          setProcessingProgress(newProgress);
          setProcessingStep(data.message || 'Processing...');
        }

        // Handle legacy completion format (data.phase === 'complete' with results)
        if (data.phase === 'complete' && data.results) {
          console.log('[SSE Debug] Processing complete (legacy format):', data.results);

          // Stop vertical progress simulation
          if (verticalProgressIntervalRef.current) {
            clearInterval(verticalProgressIntervalRef.current);
            verticalProgressIntervalRef.current = null;
          }
          setProcessingProgress(100);
          setVerticalProgressSimulation(100);

          const { horizontalUrl, verticalUrl } = data.results;
          if (horizontalUrl) setProcessedVideoUrl(horizontalUrl);
          if (verticalUrl) setVerticalVideoUrl(verticalUrl);

          enqueueSnackbar('Video processing complete!', { variant: 'success' });
          setIsProcessing(false);

          // Update Master Video List with URLs
          updateMasterVideoListUrls(horizontalUrl, verticalUrl);

          processingSSE.close();
          if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
          setEventSource(prev => prev === processingSSE ? null : prev);
        }

        // Handle errors
        if (data.phase === 'error' && data.error) {
          console.error('Processing SSE Error:', data.error);
          enqueueSnackbar(`Processing Error: ${data.error}`, { variant: 'error' });

          // Stop vertical progress simulation
          if (verticalProgressIntervalRef.current) {
            clearInterval(verticalProgressIntervalRef.current);
            verticalProgressIntervalRef.current = null;
          }

          setIsProcessing(false);
          processingSSE.close();
          if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
          setEventSource(prev => prev === processingSSE ? null : prev);
        }
      } catch (error) {
        console.error('Error parsing processing SSE data:', error);
      }
    };

    processingSSE.onerror = (event) => {
      console.error('Processing SSE connection error:', event);
      if (processingSSE.readyState === EventSource.CLOSED) {
        setIsProcessing(false);
        enqueueSnackbar('Processing connection failed', { variant: 'error' });

        // Stop vertical progress simulation
        if (verticalProgressIntervalRef.current) {
          clearInterval(verticalProgressIntervalRef.current);
          verticalProgressIntervalRef.current = null;
        }

        if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
        setEventSource(prev => prev === processingSSE ? null : prev);
      }
    };

    console.log('[LocalFile] Normalization setup complete. Video will load and be ready for processing.');
  }, [enqueueSnackbar, eventSource, processingPhase, verticalProgressSimulation, videoId, youtubeUrl, updateMasterVideoListUrls]);

  const downloadFromYoutube = useCallback(async (urlToDownload?: string) => {
    const finalUrl = urlToDownload || youtubeUrl; // Use parameter or component state youtubeUrl
    if (!finalUrl) {
      enqueueSnackbar('UploadVid: YT URL parameter is missing.', { variant: 'error' });
      return;
    }

    // Prevent duplicate downloads of the same URL
    if (activeDownloadRef.current === finalUrl) {
      console.log('[DownloadYT] Already downloading this URL, skipping duplicate request:', finalUrl);
      return;
    }

    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|v\/|shorts\/)|youtu\.be\/)[\w-]+/;
    if (!youtubeRegex.test(finalUrl.trim())) {
      enqueueSnackbar('UploadVid: YT URL parameter is invalid format.', { variant: 'error' });
      return;
    }

    activeDownloadRef.current = finalUrl;

    const currentExecutionVideoId = uuidv4(); // Generate videoId for this specific execution
    console.log(`[DownloadYT] Initiating with videoId: ${currentExecutionVideoId}`);

    // Read row index, song, and artist from URL params directly (state may not be updated yet due to async React setState)
    const urlParams = new URLSearchParams(location.search);
    const rowIndexFromUrl = urlParams.get('videoManagerRowIndex');
    const songFromUrl = urlParams.get('song');
    const artistFromUrl = urlParams.get('artist');
    const sheetRowIndexToUse = rowIndexFromUrl ? parseInt(rowIndexFromUrl, 10) : videoManagerRowIndex;
    console.log(`[DownloadYT] Sheet row index: ${sheetRowIndexToUse} (from URL: ${rowIndexFromUrl}, from state: ${videoManagerRowIndex})`);
    console.log(`[DownloadYT] Song: ${songFromUrl || songFromVideoManager} (from URL: ${songFromUrl}, from state: ${songFromVideoManager})`);
    console.log(`[DownloadYT] Artist: ${artistFromUrl || artistFromVideoManager} (from URL: ${artistFromUrl}, from state: ${artistFromVideoManager})`);

    // Use URL params first (always available), fall back to state (may be stale due to async React setState)
    let songToUse = songFromUrl || songFromVideoManager;
    let artistToUse = artistFromUrl || artistFromVideoManager;
    
    if (!songToUse || !artistToUse) {
      console.log('[DownloadYT] Song/artist metadata missing, fetching from YouTube...');
      try {
        const songInfo = await youtubeService.getSongInfo(finalUrl);
        songToUse = songToUse || songInfo.song;
        artistToUse = artistToUse || songInfo.artist;
        console.log(`[DownloadYT] Fetched metadata - Song: ${songToUse}, Artist: ${artistToUse}`);
        
        // Update local state with fetched metadata
        if (!songFromVideoManager && songInfo.song) {
          setSongFromVideoManager(songInfo.song);
        }
        if (!artistFromVideoManager && songInfo.artist) {
          setArtistFromVideoManager(songInfo.artist);
        }
      } catch (error) {
        console.warn('[DownloadYT] Failed to fetch YouTube metadata:', error);
      }
    }

    setIsInitializingDownload(false); // Clear initializing state
    setIsDownloadingFromYoutube(true);
    setDownloadFailed(false); // Clear any previous failure state
    setDownloadProgress(0);
    setDownloadStep('Starting YouTube download...');
    setSelectedFile(null);
    setVideoUrl('');
    setIsVideoFromYoutubeSource(true); 

    setVideoId(currentExecutionVideoId); // Set the main component videoId state
    currentVideoIdRef.current = currentExecutionVideoId; // Track current video ID

    // Close any previous EventSource instances
    // eventSourceRef.current is for general cleanup, direct manipulation here might be tricky
    // The main eventSource state variable should be prioritized for active connections
    if (eventSource) {
      console.log('[DownloadYT] Closing existing eventSource (state) before new download.', eventSource);
      eventSource.close();
      setEventSource(null); 
    }
    // Also ensure ref is cleared if it was somehow still holding an old one, though state should be master
    if (eventSourceRef.current) {
        console.log('[DownloadYT] Closing existing eventSourceRef.current before new download.');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
    }


    // Will store YouTube API SSE connection after we get the progressUrl
    let youtubeApiSSE: EventSource | null = null;

    try {
      const apiUrl = process.env.NODE_ENV === 'development'
        ? `${SERVER_BASE_URL}/api/download-youtube`
        : '/api/download-youtube';

      console.log('[DownloadYT] Calling server to queue YouTube download...');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10 * 60 * 1000); // 10 minute timeout

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          videoId: currentExecutionVideoId,
          youtubeUrl: finalUrl.trim(),
          song: songToUse,
          artist: artistToUse,
          sheetRowIndex: sheetRowIndexToUse
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Download failed: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || result.error || 'Download failed');
      }

      const { ytDownloadJobId, progressUrl, importJobId } = result;

      if (!ytDownloadJobId || !progressUrl) {
        throw new Error('Server did not return job ID or progress URL');
      }

      console.log('[DownloadYT] Download queued. Job ID:', ytDownloadJobId);
      console.log('[DownloadYT] Progress URL:', progressUrl);
      console.log('[DownloadYT] Import Job ID:', importJobId);

      // Store importJobId for polling fallback
      if (importJobId) {
        processedJobIdRef.current = importJobId;
      }

      // Connect to progress SSE via the authed Billboard server proxy (holds the
      // downloader secret). progressUrl is a relative /api/... path; authedEventSource
      // attaches the JWT. (Never connect to the downloader directly — CORS + 401.)
      const sseUrl = /^https?:\/\//i.test(progressUrl) ? progressUrl : `${SERVER_BASE_URL}${progressUrl}`;
      console.log('[DownloadYT] Connecting to progress SSE (via server):', sseUrl);
      youtubeApiSSE = await authedEventSource(sseUrl);
      setEventSource(youtubeApiSSE);
      eventSourceRef.current = youtubeApiSSE;

      youtubeApiSSE.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[YouTube API SSE] Event:', data);

          // Ignore events from old downloads
          if (currentVideoIdRef.current !== currentExecutionVideoId) {
            console.log(`[YouTube API SSE] Ignoring event for old videoId ${currentExecutionVideoId}`);
            return;
          }

          // Handle queued status
          if (data.status === 'queued') {
            setDownloadStep('Download queued...');
            setDownloadProgress(0);
          }

          // Handle downloading status with progress
          if (data.status === 'downloading') {
            const percent = data.progress_percent || 0;
            const speed = data.speed || '';
            const eta = data.eta || '';

            setDownloadProgress(percent);

            // Build detailed progress message
            let progressMsg = `Downloading: ${percent.toFixed(1)}%`;
            if (speed) progressMsg += ` | ${speed}`;
            if (eta) progressMsg += ` | ETA ${eta}`;

            setDownloadStep(progressMsg);
            console.log(`[YouTube API SSE] Progress: ${percent}% | ${speed} | ${eta}`);
          }

          // Handle completion
          if (data.status === 'completed') {
            console.log('[YouTube API SSE] Download completed!', data.result);

            // Close SSE
            if (youtubeApiSSE) {
              youtubeApiSSE.close();
              if (eventSourceRef.current === youtubeApiSSE) eventSourceRef.current = null;
              setEventSource(prev => prev === youtubeApiSSE ? null : prev);
            }

            setDownloadProgress(100);
            setDownloadStep('Download complete! Fetching video...');

            // Call our server to download the file
            const completeApiUrl = process.env.NODE_ENV === 'development'
              ? `${SERVER_BASE_URL}/api/youtube-download-complete`
              : '/api/youtube-download-complete';

            fetch(completeApiUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ytDownloadJobId,
                downloadUrl: data.result.download_url,
                filename: data.result.filename,
                title: data.result.title
              })
            })
              .then(res => res.json())
              .then(async (completeResult) => {
                if (!completeResult.success) {
                  throw new Error(completeResult.error || 'File download failed');
                }

                console.log('[DownloadYT] Server downloaded file successfully');

                // Fetch import job to get video URL for cropping
                const jobId = completeResult.importJobId || processedJobIdRef.current;
                if (!jobId) {
                  throw new Error('No import job ID available');
                }

                const jobApiUrl = process.env.NODE_ENV === 'development'
                  ? `${SERVER_BASE_URL}/api/import-manager/jobs/${jobId}`
                  : `/api/import-manager/jobs/${jobId}`;

                const jobResponse = await fetch(jobApiUrl);
                const jobData = await jobResponse.json();

                if (!jobData.success || !jobData.job) {
                  throw new Error('Failed to fetch import job');
                }

                const job = jobData.job;
                const videoUrl = (job.youtube_download_path ? `${SERVER_BASE_URL}${job.youtube_download_path.replace('/app/server', '')}` : null) ||
                  job.youtube_download_s3_url;

                if (!videoUrl) {
                  throw new Error('No video URL in job data');
                }

                console.log('[DownloadYT] Loading video for crop:', videoUrl);
                setVideoUrl(videoUrl);
                setServerVideoPath(job.youtube_download_path || job.youtube_download_s3_url);
                setIsVideoFromYoutubeSource(true);
                setIsDownloadingFromYoutube(false);
                enqueueSnackbar('Video downloaded! Select crop area.', { variant: 'success' });
              })
              .catch(err => {
                console.error('[DownloadYT] Error in completion handler:', err);
                enqueueSnackbar(`Download completion failed: ${err.message}`, { variant: 'error' });
                setIsDownloadingFromYoutube(false);
                setDownloadFailed(true);
                activeDownloadRef.current = null;
              });
          }

          // Handle failure
          if (data.status === 'failed') {
            console.error('[YouTube API SSE] Download failed:', data.error);

            if (youtubeApiSSE) {
              youtubeApiSSE.close();
              if (eventSourceRef.current === youtubeApiSSE) eventSourceRef.current = null;
              setEventSource(prev => prev === youtubeApiSSE ? null : prev);
            }

            enqueueSnackbar(`Download failed: ${data.error}`, { variant: 'error' });
            setIsDownloadingFromYoutube(false);
            setDownloadFailed(true);
            setDownloadProgress(0);
            setDownloadStep('Download failed');
            activeDownloadRef.current = null;
          }

        } catch (error) {
          console.error('[YouTube API SSE] Error parsing event:', error);
        }
      };

      youtubeApiSSE.onerror = (event) => {
        console.error('[YouTube API SSE] Connection error:', event);
        console.log('[YouTube API SSE] ReadyState:', youtubeApiSSE?.readyState);

        if (youtubeApiSSE && youtubeApiSSE.readyState === EventSource.CLOSED) {
          if (eventSourceRef.current === youtubeApiSSE) eventSourceRef.current = null;
          setEventSource(prev => prev === youtubeApiSSE ? null : prev);

          // If we have an import job ID, poll for completion as fallback
          if (processedJobIdRef.current) {
            console.log('[YouTube API SSE] Connection dropped, starting polling fallback');
            pollForDownloadCompletion(processedJobIdRef.current);
          } else {
            enqueueSnackbar('Download connection lost', { variant: 'warning' });
            setIsDownloadingFromYoutube(false);
            activeDownloadRef.current = null;
          }
        }
      };

      enqueueSnackbar('YouTube download started. Connecting to progress stream...', { variant: 'info' });
      setDownloadStep('Connecting to download service...');

    } catch (error) {
      console.error('YouTube download error:', error);
      enqueueSnackbar(`Download failed: ${error instanceof Error ? error.message : String(error)}`, { variant: 'error' });
      setIsDownloadingFromYoutube(false);
      setDownloadFailed(true);
      setDownloadProgress(0);
      setDownloadStep('Download failed');

      // Close SSE if it was created
      if (youtubeApiSSE && youtubeApiSSE.readyState !== EventSource.CLOSED) {
        youtubeApiSSE.close();
      }
      if (eventSourceRef.current === youtubeApiSSE) eventSourceRef.current = null;
      setEventSource(prev => prev === youtubeApiSSE ? null : prev);

      // Clear download ref on error
      activeDownloadRef.current = null;
    }
  }, [
    // Only include stable dependencies that don't change during processing
    enqueueSnackbar,
    resetForm,
    videoRef,
    eventSourceRef,
    pollForCompletion,
    pollForDownloadCompletion,
    updateMasterVideoListUrls
    // Removed frequently changing deps: youtubeUrl, eventSource, showRetryVertical, songFromVideoManager, artistFromVideoManager
    // The function now reads these values directly from state when called, rather than capturing them in closure
  ]);

  // Effect to automatically start download when component mounts with YouTube URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlFromQuery = params.get('youtubeUrl');
    const localVideoPathFromQuery = params.get('localVideoPath');
    const jobIdFromQuery = params.get('jobId');
    const rowIndex = params.get('videoManagerRowIndex');
    const song = params.get('song');
    const artist = params.get('artist');
    const verticalMode = params.get('verticalMode');
    const horizontalS3Url = params.get('horizontalS3Url');
    const returnUrl = params.get('returnUrl');
    const returnSlot = params.get('returnSlot');

    console.log('[UploadVideo] URL Parameters extracted:');
    console.log('  urlFromQuery:', urlFromQuery);
    console.log('  localVideoPathFromQuery:', localVideoPathFromQuery);
    console.log('  jobIdFromQuery:', jobIdFromQuery);
    console.log('  rowIndex:', rowIndex);
    console.log('  song:', song);
    console.log('  artist:', artist);
    console.log('  verticalMode:', verticalMode);
    console.log('  horizontalS3Url:', horizontalS3Url);
    console.log('  returnUrl:', returnUrl);
    console.log('  returnSlot:', returnSlot);
    console.log('  processedJobIdRef:', processedJobIdRef.current);

    // Handle vertical-only mode (when horizontal video already exists)
    if (verticalMode === 'true' && horizontalS3Url) {
      // Prevent duplicate processing
      if (processedQueryParamsRef.current) {
        console.log('[UploadVideo] Vertical mode already processed, skipping');
        return;
      }
      processedQueryParamsRef.current = true;

      console.log('[UploadVideo] Vertical-only mode with horizontal URL:', horizontalS3Url);

      // Set metadata
      if (song) setSongFromVideoManager(song);
      if (artist) setArtistFromVideoManager(artist);
      if (rowIndex) setVideoManagerRowIndex(parseInt(rowIndex, 10));

      // Load horizontal video in player
      setVideoUrl(horizontalS3Url);
      setProcessedVideoUrl(horizontalS3Url);

      // Show vertical processing UI
      setIsProcessing(true);
      setProcessingPhase('vertical');
      setProcessingProgress(-1); // Indeterminate
      setProcessingStep('Generating vertical video...');

      // Generate videoId and start vertical generation
      const newVideoId = uuidv4();
      setVideoId(newVideoId);

      // Create import job and start vertical generation
      startVerticalOnlyGeneration(horizontalS3Url, newVideoId, song, artist, rowIndex);
      return; // Don't continue with other URL param handling
    }

    // Check if we're switching to a different job
    const isSameJob = jobIdFromQuery && processedJobIdRef.current === jobIdFromQuery;
    const isNewJob = jobIdFromQuery && processedJobIdRef.current !== jobIdFromQuery;

    // Skip if already processed this exact job (prevents duplicate processing on re-renders)
    if (isSameJob) {
      console.log('[UploadVideo] Same job already processed, skipping');
      return;
    }

    // If switching to a new job, clean up old state and SSE connections
    if (isNewJob && processedJobIdRef.current) {
      console.log('[UploadVideo] Switching from job', processedJobIdRef.current, 'to', jobIdFromQuery);

      // Close existing SSE connection
      if (eventSourceRef.current) {
        console.log('[UploadVideo] Closing old SSE connection');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setEventSource(null);
      }

      // Reset state for new job
      setIsProcessing(false);
      setProcessingProgress(0);
      setIsDownloadingFromYoutube(false);
      setDownloadProgress(0);
      setVideoUrl('');
      setServerVideoPath('');
      setVideoId(null);
      processedQueryUrlRef.current = null;
    }

    // For non-jobId URLs, use the old guard to prevent duplicate processing
    if (!jobIdFromQuery && processedQueryParamsRef.current) {
      return;
    }

    // Mark as processed (for non-jobId cases)
    if (!jobIdFromQuery) {
      processedQueryParamsRef.current = true;
    }

    if (jobIdFromQuery) {
      // Track which jobId we're processing
      processedJobIdRef.current = jobIdFromQuery;
      // Load video from import job
      console.log(`[UploadVideo] Loading import job: ${jobIdFromQuery}`);

      // Set processedQueryUrlRef SYNCHRONOUSLY to prevent duplicate downloads
      // (the else-if urlFromQuery check runs before this async fetch completes)
      processedQueryUrlRef.current = jobIdFromQuery;

      fetch(`${SERVER_BASE_URL}/api/import-manager/jobs/${jobIdFromQuery}`)
        .then(res => res.json())
        .then(async data => {
          if (data.success && data.job) {
            const job = data.job;
            console.log('[UploadVideo] Loaded import job:', job);

            // Handle COMPLETED jobs - redirect appropriately
            if (job.status === 'completed') {
              console.log('[UploadVideo] Import job already completed, redirecting');
              enqueueSnackbar('This video has already been processed! Redirecting...', { variant: 'info' });
              navigateAfterCompletion(job.vertical_s3_url, 1500);
              return;
            }

            // Handle FAILED jobs
            if (job.status === 'failed') {
              console.log('[UploadVideo] Import job failed:', job.error_message);
              enqueueSnackbar(`Import job failed: ${job.error_message || 'Unknown error'}`, { variant: 'error' });
              return;
            }

            // Check for generating/processing status FIRST - these jobs have video_id and path but need to show progress
            if ((job.status === 'generating_horizontal' || job.status === 'generating_vertical' ||
                 job.status === 'processing_h' || job.status === 'processing_v') && job.video_id) {
              console.log('[UploadVideo] Import job is generating, showing progress for video_id:', job.video_id);
              console.log('[UploadVideo] Job data:', {
                status: job.status,
                youtube_download_path: job.youtube_download_path,
                youtube_download_s3_url: job.youtube_download_s3_url,
                horizontal_s3_url: job.horizontal_s3_url,
              });

              // Set metadata
              if (job.song) setSongFromVideoManager(job.song);
              if (job.artist) setArtistFromVideoManager(job.artist);
              if (job.sheet_row_index !== null && job.sheet_row_index !== undefined) {
                setVideoManagerRowIndex(job.sheet_row_index);
              }

              // Load the source video for preview
              // Priority: local path > horizontal S3 URL (for vertical-only jobs)
              if (job.youtube_download_path) {
                // Use local path for preview (works for both horizontal and vertical generation)
                const filename = job.youtube_download_path.split('/').pop();
                const videoPath = `uploads/${filename}`;
                const fullVideoUrl = `${SERVER_BASE_URL}/${videoPath}`;
                console.log('[UploadVideo] Loading source video from local path:', fullVideoUrl);
                setVideoUrl(fullVideoUrl);
                setServerVideoPath(videoPath);
              } else if (job.horizontal_s3_url) {
                // For vertical-only jobs, load the horizontal S3 URL
                console.log('[UploadVideo] Loading horizontal video from S3:', job.horizontal_s3_url);
                setVideoUrl(job.horizontal_s3_url);
                setProcessedVideoUrl(job.horizontal_s3_url);
              } else {
                // Can't load video - just show progress
                console.log('[UploadVideo] No video available for preview, showing progress only');
              }

              setVideoId(job.video_id);
              setIsVideoFromYoutubeSource(true);
              setIsProcessing(true); // Show processing UI

              // Different progress tracking for horizontal vs vertical
              if (job.status === 'generating_horizontal' || job.status === 'processing_h') {
                setProcessingProgress(0);
                setProcessingPhase('horizontal');
                // Horizontal generation uses SSE for real-time progress
                const processingSseUrl = `${SERVER_BASE_URL}/api/video-progress/${encodeURIComponent(job.video_id)}`;
                console.log('[UploadVideo] Creating SSE for horizontal generation progress:', processingSseUrl);
                const processingSSE = await authedEventSource(processingSseUrl);
                setEventSource(processingSSE);
                eventSourceRef.current = processingSSE;

                processingSSE.onmessage = (event) => {
                  try {
                    const sseData = JSON.parse(event.data);
                    console.log('[SSE Horizontal] Received:', sseData);

                    // Handle FINAL completion first (phase='completed' with verticalUrl)
                    if ((sseData.phase === 'completed' || sseData.phase === 'complete') && sseData.completed === true && sseData.verticalUrl) {
                      console.log('[SSE Horizontal] FINAL completion - vertical video ready:', sseData.verticalUrl);

                      // Stop any polling
                      if (verticalPollRef.current) {
                        clearTimeout(verticalPollRef.current);
                        verticalPollRef.current = null;
                      }
                      verticalPollActiveRef.current = false;

                      setProcessingProgress(100);
                      setProcessingStep('Complete!');
                      setIsProcessing(false);
                      setVerticalVideoUrl(sseData.verticalUrl);

                      enqueueSnackbar('Video processing complete!', { variant: 'success' });

                      // Close SSE connection to prevent reconnect loop
                      processingSSE.close();
                      setEventSource(null);
                      eventSourceRef.current = null;

                      // Navigate after completion - either to return URL or video manager
                      navigateAfterCompletion(sseData.verticalUrl);
                      return;
                    }

                    // Check for horizontal completion - switch to vertical indeterminate progress
                    if (sseData.phase === 'horizontal_complete' ||
                        sseData.phase === 'vertical_generation' || sseData.phase === 'vertical_processing' ||
                        sseData.phase === 'vertical_polling' || sseData.phase === 'vertical_download' ||
                        sseData.phase === 'video_manager_update') {
                      console.log('[SSE Horizontal] Horizontal complete, switching to vertical indeterminate progress');
                      setProcessingPhase('vertical');
                      setProcessingProgress(-1); // Indeterminate progress bar
                      setProcessingStep(sseData.message || 'Generating vertical video...');
                      // Start polling for vertical completion (this flow has jobId)
                      // Also set the row index for completion highlighting
                      if (job.sheet_row_index !== null && job.sheet_row_index !== undefined) {
                        setVideoManagerRowIndex(job.sheet_row_index);
                      }
                      startVerticalCompletionPolling();
                      return;
                    }

                    // Only update progress for horizontal phase (not vertical)
                    if (sseData.percent !== undefined && sseData.phase !== 'vertical_generation' &&
                        sseData.phase !== 'vertical_processing' && sseData.phase !== 'vertical_polling' &&
                        sseData.phase !== 'completed') {
                      setProcessingProgress(Math.round(sseData.percent));
                    }
                  } catch (e) {
                    console.error('[SSE Horizontal] Parse error:', e);
                  }
                };

                processingSSE.onerror = () => {
                  console.log('[SSE Horizontal] Connection error');
                  processingSSE.close();
                  setEventSource(null);
                  eventSourceRef.current = null;
                };
              } else {
                // Vertical generation uses AutoCrop webhook - poll job status instead
                console.log('[UploadVideo] Vertical generation - polling job status (AutoCrop uses webhook, not SSE)');
                setProcessingProgress(-1); // -1 indicates indeterminate progress (spinner)
                setProcessingPhase('vertical');
                setProcessingStep('Generating vertical video...');

                // Use the proper polling function that handles refs and cleanup
                startVerticalCompletionPolling();
              }

            } else if (job.video_id && (job.youtube_download_s3_url || job.youtube_download_path)) {
              let fullVideoUrl: string = '';
              let videoPath: string = '';

              // TEMPORARY: Prefer local path to avoid CORS issues with S3
              // TODO: Once S3 CORS is configured, switch back to preferring S3 by swapping the if/else conditions
              if (job.youtube_download_path) {
                // Use local path (avoids CORS issues)
                const filename = job.youtube_download_path.split('/').pop();
                videoPath = `uploads/${filename}`;
                fullVideoUrl = `${SERVER_BASE_URL}/${videoPath}`;
                console.log('[UploadVideo] Loading video from local storage:', fullVideoUrl);
              } else if (job.youtube_download_s3_url) {
                // Fall back to S3 URL if local path not available
                fullVideoUrl = job.youtube_download_s3_url;
                videoPath = job.youtube_download_s3_url; // Use S3 URL as path for S3 videos
                console.log('[UploadVideo] Loading video from S3:', fullVideoUrl);
              }

              console.log('[UploadVideo] Using video ID:', job.video_id);

              // Set video URL, server path, and videoId
              setVideoUrl(fullVideoUrl);
              setServerVideoPath(videoPath);
              setVideoId(job.video_id);
              setIsVideoFromYoutubeSource(true);

              // Set song/artist metadata
              if (job.song) setSongFromVideoManager(job.song);
              if (job.artist) setArtistFromVideoManager(job.artist);

              processedQueryUrlRef.current = jobIdFromQuery;
            } else if (job.status === 'downloading' || job.status === 'pending') {
              // Job is still downloading - show progress UI and set up SSE listener
              console.log('[UploadVideo] Import job still downloading, reconnecting to progress stream');
              console.log('[UploadVideo] Job has yt_download_job_id:', job.yt_download_job_id);
              console.log('[UploadVideo] Job fields:', Object.keys(job));

              if (job.song) setSongFromVideoManager(job.song);
              if (job.artist) setArtistFromVideoManager(job.artist);

              if (job.video_id) {
                // Set up SSE listener for existing download progress
                setVideoId(job.video_id);
                setIsVideoFromYoutubeSource(true);
                setIsDownloadingFromYoutube(true); // Show download progress UI
                setDownloadProgress(0);
                setDownloadStep('Checking download status...');
                setYoutubeUrl(job.youtube_url || urlFromQuery || '');
                processedQueryUrlRef.current = jobIdFromQuery;

                // If we have YouTube Download API job ID, first check if it failed before trying to reconnect
                if (job.yt_download_job_id) {
                  // Check job status via the authed Billboard server (holds the
                  // downloader secret); never call the downloader directly.
                  console.log('[UploadVideo] Checking YouTube job status first...');
                  fetch(`${SERVER_BASE_URL}/api/youtube-job-status/${job.yt_download_job_id}`)
                    .then(res => res.json())
                    .then(async (statusData) => {
                      console.log('[UploadVideo] YouTube API job status:', statusData.status);

                      if (statusData.status === 'failed') {
                        // Download failed - start a fresh download
                        console.log('[UploadVideo] Previous download failed, starting fresh download');
                        setIsDownloadingFromYoutube(false);
                        setDownloadStep('');

                        // Reset the job status to pending and clear old yt_download_job_id
                        fetch(`${SERVER_BASE_URL}/api/import-manager/jobs/${job.id}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            status: 'pending',
                            error_message: null,
                            yt_download_job_id: null
                          })
                        }).then(() => {
                          // Start fresh download
                          enqueueSnackbar('Previous download failed. Starting fresh download...', { variant: 'info' });
                          downloadFromYoutube(job.youtube_url || urlFromQuery);
                        });
                        return;
                      }

                      if (statusData.status === 'completed') {
                        // Download already completed - call backend to process
                        console.log('[UploadVideo] Download already completed, processing...');
                        setDownloadStep('Download complete! Fetching video...');
                        setDownloadProgress(100);

                        fetch(`${SERVER_BASE_URL}/api/youtube-download-complete`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({
                            ytDownloadJobId: job.yt_download_job_id,
                            downloadUrl: statusData.result?.download_url,
                            filename: statusData.result?.filename,
                            title: statusData.result?.title
                          })
                        })
                          .then(res => res.json())
                          .then(async (completeResult) => {
                            if (!completeResult.success) {
                              throw new Error(completeResult.error || 'Failed to complete download');
                            }

                            // Fetch job to get video URL
                            const jobResponse = await fetch(`${SERVER_BASE_URL}/api/import-manager/jobs/${jobIdFromQuery}`);
                            const jobData = await jobResponse.json();

                            if (jobData.success && jobData.job) {
                              const updatedJob = jobData.job;
                              const videoUrl = updatedJob.youtube_download_path
                                ? `${SERVER_BASE_URL}${updatedJob.youtube_download_path.replace('/app/server', '')}`
                                : updatedJob.youtube_download_s3_url;

                              if (videoUrl) {
                                console.log('[UploadVideo] Loading video for crop:', videoUrl);
                                setVideoUrl(videoUrl);
                                setServerVideoPath(updatedJob.youtube_download_path || updatedJob.youtube_download_s3_url);
                                setIsVideoFromYoutubeSource(true);
                                setIsDownloadingFromYoutube(false);
                                enqueueSnackbar('Video downloaded! Select crop area.', { variant: 'success' });
                              }
                            }
                          })
                          .catch(err => {
                            console.error('[UploadVideo] Error completing download:', err);
                            enqueueSnackbar(`Error: ${err.message}`, { variant: 'error' });
                            setIsDownloadingFromYoutube(false);
                          });
                        return;
                      }

                      // Download still in progress - connect to SSE for updates,
                      // proxied through the authed Billboard server (holds the secret).
                      setDownloadStep('Reconnecting to download...');
                      const progressUrl = `${SERVER_BASE_URL}/api/youtube-job-progress/${job.yt_download_job_id}`;
                      console.log('[UploadVideo] Reconnecting to YouTube job SSE (via server):', progressUrl);
                      const reconnectSSE = await authedEventSource(progressUrl);
                      setEventSource(reconnectSSE);
                      eventSourceRef.current = reconnectSSE;

                      reconnectSSE.onmessage = (event) => {
                        try {
                          const data = JSON.parse(event.data);
                          console.log('[YouTube API SSE Reconnect]:', data);

                          if (data.status === 'downloading') {
                            const percent = data.progress_percent || 0;
                            const speed = data.speed || '';
                            const eta = data.eta || '';
                            setDownloadProgress(percent);
                            let msg = `Downloading: ${percent.toFixed(1)}%`;
                            if (speed) msg += ` | ${speed}`;
                            if (eta) msg += ` | ETA ${eta}`;
                            setDownloadStep(msg);
                          }

                          if (data.status === 'completed') {
                            reconnectSSE.close();
                            setEventSource(null);
                            eventSourceRef.current = null;

                            // Call backend to download the file and update job
                            console.log('[YouTube API SSE Reconnect] Download completed, notifying backend...');
                            setDownloadStep('Download complete! Fetching video...');
                            setDownloadProgress(100);

                            fetch(`${SERVER_BASE_URL}/api/youtube-download-complete`, {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({
                                ytDownloadJobId: job.yt_download_job_id,
                                downloadUrl: data.result?.download_url,
                                filename: data.result?.filename,
                                title: data.result?.title
                              })
                            })
                              .then(res => res.json())
                              .then(async (completeResult) => {
                                if (!completeResult.success) {
                                  throw new Error(completeResult.error || 'Failed to complete download');
                                }

                                // Fetch job to get video URL
                                const jobResponse = await fetch(`${SERVER_BASE_URL}/api/import-manager/jobs/${jobIdFromQuery}`);
                                const jobData = await jobResponse.json();

                                if (jobData.success && jobData.job) {
                                  const updatedJob = jobData.job;
                                  const videoUrl = updatedJob.youtube_download_path
                                    ? `${SERVER_BASE_URL}${updatedJob.youtube_download_path.replace('/app/server', '')}`
                                    : updatedJob.youtube_download_s3_url;

                                  if (videoUrl) {
                                    console.log('[YouTube API SSE Reconnect] Loading video for crop:', videoUrl);
                                    setVideoUrl(videoUrl);
                                    setServerVideoPath(updatedJob.youtube_download_path || updatedJob.youtube_download_s3_url);
                                    setIsVideoFromYoutubeSource(true);
                                    setIsDownloadingFromYoutube(false);
                                    enqueueSnackbar('Video downloaded! Select crop area.', { variant: 'success' });
                                  }
                                }
                              })
                              .catch(err => {
                                console.error('[YouTube API SSE Reconnect] Error completing download:', err);
                                enqueueSnackbar(`Error: ${err.message}`, { variant: 'error' });
                                setIsDownloadingFromYoutube(false);
                              });
                          }

                          if (data.status === 'failed') {
                            reconnectSSE.close();
                            setEventSource(null);
                            eventSourceRef.current = null;
                            setIsDownloadingFromYoutube(false);
                            enqueueSnackbar('YouTube download failed', { variant: 'error' });
                          }
                        } catch (err) {
                          console.error('[YouTube API SSE Reconnect] Parse error:', err);
                        }
                      };

                      reconnectSSE.onerror = () => {
                        console.log('[YouTube API SSE Reconnect] Connection failed, falling back to polling');
                        reconnectSSE.close();
                        setEventSource(null);
                        eventSourceRef.current = null;
                        pollForDownloadCompletion(jobIdFromQuery);
                      };
                    })
                    .catch(err => {
                      console.error('[UploadVideo] Error checking YouTube API status:', err);
                      // Fall back to trying SSE anyway
                      setDownloadStep('Reconnecting to download...');
                      pollForDownloadCompletion(jobIdFromQuery);
                    });
                } else {
                  // Fallback to old server SSE endpoint (for jobs created before this feature)
                  const reconnectSseUrl = `${SERVER_BASE_URL}/api/video-progress/${encodeURIComponent(job.video_id)}`;
                  console.log('[UploadVideo] Using legacy SSE endpoint:', reconnectSseUrl);
                  const reconnectSSE = await authedEventSource(reconnectSseUrl);
                setEventSource(reconnectSSE);
                eventSourceRef.current = reconnectSSE;

                // Track if polling should stop (video loaded or job not found)
                let pollingCancelled = false;

                // Define polling function that can be used by both onmessage and onerror
                const pollForVideoUrl = (attempt: number, maxAttempts: number) => {
                  // Check if polling was cancelled (video already loaded or job deleted)
                  if (pollingCancelled) {
                    console.log('[SSE Reconnect] Polling cancelled, stopping');
                    return;
                  }

                  console.log(`[SSE Reconnect] Polling attempt ${attempt}/${maxAttempts}`);

                  fetch(`${SERVER_BASE_URL}/api/import-manager/jobs/${jobIdFromQuery}`)
                    .then(res => res.json())
                    .then(data => {
                      if (pollingCancelled) return; // Check again after async

                      if (data.success && data.job) {
                        const polledJob = data.job;
                        console.log(`[SSE Reconnect] Poll ${attempt} - status: ${polledJob.status}, path: ${polledJob.youtube_download_path || 'none'}`);

                        if (polledJob.youtube_download_path || polledJob.youtube_download_s3_url) {
                          // Video URL is ready - cancel polling first
                          pollingCancelled = true;
                          let videoUrl = '';
                          if (polledJob.youtube_download_path) {
                            const filename = polledJob.youtube_download_path.split('/').pop();
                            videoUrl = `${SERVER_BASE_URL}/uploads/${filename}`;
                          } else {
                            videoUrl = polledJob.youtube_download_s3_url;
                          }
                          console.log('[SSE Reconnect] Video ready, loading for crop:', videoUrl);
                          setVideoUrl(videoUrl);
                          setServerVideoPath(polledJob.youtube_download_path || polledJob.youtube_download_s3_url);
                          setIsDownloadingFromYoutube(false);
                          setDownloadStep('');
                          enqueueSnackbar('Video ready for cropping', { variant: 'success' });
                        } else if (attempt < maxAttempts) {
                          // Not ready yet, try again
                          console.log(`[SSE Reconnect] Video not ready, retrying in 2 seconds...`);
                          setTimeout(() => pollForVideoUrl(attempt + 1, maxAttempts), 2000);
                        } else {
                          // Timed out
                          console.error('[SSE Reconnect] Timed out waiting for video URL');
                          setIsDownloadingFromYoutube(false);
                          enqueueSnackbar('Video download completed but failed to load. Please refresh.', { variant: 'warning' });
                        }
                      } else {
                        // Job not found or error - stop polling (job was likely deleted)
                        console.error('[SSE Reconnect] Job not found, stopping poll:', data);
                        pollingCancelled = true;
                        // Don't show error - the job may have been deleted intentionally
                        // or this is a stale polling loop from navigation
                      }
                    })
                    .catch(err => {
                      if (pollingCancelled) return;
                      console.error('[SSE Reconnect] Poll error:', err);
                      if (attempt < maxAttempts) {
                        setTimeout(() => pollForVideoUrl(attempt + 1, maxAttempts), 2000);
                      }
                    });
                };

                reconnectSSE.onmessage = (event) => {
                  try {
                    const sseData = JSON.parse(event.data);
                    console.log('[SSE Reconnect] Received:', sseData);

                    // Handle download complete - refetch job to get video URL
                    // Check for: phase='download_complete', phase='youtube_complete', or percent=100
                    const isComplete = sseData.phase === 'download_complete' ||
                                       sseData.status === 'download_complete' ||
                                       sseData.phase === 'youtube_complete' ||
                                       sseData.percent === 100;

                    if (isComplete) {
                      console.log('[SSE Reconnect] Download complete, polling for video URL');
                      reconnectSSE.close();
                      setEventSource(null);
                      eventSourceRef.current = null;
                      // Start polling after 1 second, up to 90 attempts (3 minutes total)
                      setTimeout(() => pollForVideoUrl(1, 90), 1000);
                    } else if (sseData.phase && sseData.percent !== undefined) {
                      // Regular progress update
                      setDownloadStep(sseData.message || 'Downloading...');
                    }
                  } catch (e) {
                    console.error('[SSE Reconnect] Parse error:', e);
                  }
                };

                reconnectSSE.onerror = () => {
                  console.log('[SSE Reconnect] Connection error, starting polling for completion');
                  reconnectSSE.close();
                  setEventSource(null);
                  eventSourceRef.current = null;
                  // Start polling immediately on error, up to 90 attempts (3 minutes total)
                  pollForVideoUrl(1, 90);
                };
                }
              } else if (job.youtube_url || urlFromQuery) {
                // No video_id yet, start fresh download
                const ytUrl = job.youtube_url || urlFromQuery;
                console.log('[UploadVideo] No video_id, starting download for:', ytUrl);
                setYoutubeUrl(ytUrl!);
                setIsDownloadingFromYoutube(true); // Show download progress UI
                processedQueryUrlRef.current = ytUrl;
              }
            } else {
              // Missing required fields for completed/crop_needed job
              console.error('[UploadVideo] Import job missing required fields:', {
                status: job.status,
                hasVideoId: !!job.video_id,
                hasS3Url: !!job.youtube_download_s3_url,
                hasLocalPath: !!job.youtube_download_path,
                job
              });
              enqueueSnackbar(`Cannot load import job: Missing ${!job.video_id ? 'video ID' : 'video file'}`, { variant: 'error' });
            }
          } else {
            console.error('[UploadVideo] Failed to load import job:', data);
            enqueueSnackbar('Import job not found', { variant: 'error' });
          }
        })
        .catch(err => {
          console.error('[UploadVideo] Failed to load import job:', err);
          enqueueSnackbar('Failed to load video from import job', { variant: 'error' });
        });
    } else if (urlFromQuery && !processedQueryUrlRef.current) {
      console.log(`[UploadVideo] Auto-starting download for URL: ${urlFromQuery}`);
      console.log(`[UploadVideo] Received metadata - Row: ${rowIndex}, Artist: ${artist}, Song: ${song}`);

      // Show initializing spinner immediately
      setIsInitializingDownload(true);

      processedQueryUrlRef.current = urlFromQuery;
      setYoutubeUrl(urlFromQuery);

      if (rowIndex) {
        console.log('[UploadVideo] Setting videoManagerRowIndex to:', parseInt(rowIndex, 10));
        setVideoManagerRowIndex(parseInt(rowIndex, 10));
      }
      if (song) {
        console.log('[UploadVideo] Setting songFromVideoManager to:', song);
        setSongFromVideoManager(song);
      }
      if (artist) {
        console.log('[UploadVideo] Setting artistFromVideoManager to:', artist);
        setArtistFromVideoManager(artist);
      }
      if (returnUrl) {
        console.log('[UploadVideo] Setting returnUrlFromTease to:', returnUrl);
        setReturnUrlFromTease(returnUrl);
      }
      if (returnSlot) {
        console.log('[UploadVideo] Setting returnSlotFromTease to:', returnSlot);
        setReturnSlotFromTease(returnSlot);
      }

      downloadFromYoutube(urlFromQuery);
    } else if (localVideoPathFromQuery && !processedQueryUrlRef.current) {
      console.log(`[UploadVideo] Processing local video file: ${localVideoPathFromQuery}`);
      console.log(`[UploadVideo] Received metadata - Row: ${rowIndex}, Artist: ${artist}, Song: ${song}`);

      processedQueryUrlRef.current = localVideoPathFromQuery;

      if (rowIndex) {
        console.log('[UploadVideo] Setting videoManagerRowIndex to:', parseInt(rowIndex, 10));
        setVideoManagerRowIndex(parseInt(rowIndex, 10));
      }
      if (song) {
        console.log('[UploadVideo] Setting songFromVideoManager to:', song);
        setSongFromVideoManager(song);
      }
      if (artist) {
        console.log('[UploadVideo] Setting artistFromVideoManager to:', artist);
        setArtistFromVideoManager(artist);
      }

      // Start normalization process directly with local file
      startNormalizationWithLocalFile(localVideoPathFromQuery);
    } else if (!urlFromQuery && !localVideoPathFromQuery) {
      console.log('[UploadVideo] No youtubeUrl or localVideoPath in params, not auto-downloading');
    } else if (processedQueryUrlRef.current) {
      console.log('[UploadVideo] Already processed URL:', processedQueryUrlRef.current, '- not downloading again');

      // Still set the metadata if we haven't already
      if (rowIndex && videoManagerRowIndex === null) {
        console.log('[UploadVideo] Re-setting videoManagerRowIndex to:', parseInt(rowIndex, 10));
        setVideoManagerRowIndex(parseInt(rowIndex, 10));
      }
      if (song && !songFromVideoManager) {
        console.log('[UploadVideo] Re-setting songFromVideoManager to:', song);
        setSongFromVideoManager(song);
      }
      if (artist && !artistFromVideoManager) {
        console.log('[UploadVideo] Re-setting artistFromVideoManager to:', artist);
        setArtistFromVideoManager(artist);
      }
    }

    // Cleanup function - close SSE when component unmounts or URL changes
    return () => {
      console.log('[UploadVideo] Cleanup: closing SSE connections');
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [location.search, startVerticalOnlyGeneration]); // Only run when location.search changes, not on every state change

  // Generate blurred background preview for 4:3 videos
  // Moved up and wrapped in useCallback
  const generateBlurredPreview = useCallback(async (metadata?: VideoMetadata): Promise<string | null> => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const metadataToUse = metadata || videoMetadata; // Use passed metadata or state metadata
    
    if (!video || !canvas || !metadataToUse) {
      console.error('[Blurred Preview] Missing dependencies:', { 
        hasVideo: !!video, 
        hasCanvas: !!canvas, 
        hasMetadata: !!metadataToUse 
      });
      return null;
    }

    try {
      console.log('[Blurred Preview] Starting preview generation...');
      console.log('[Blurred Preview] Using metadata:', metadataToUse);
      console.log('[Blurred Preview] Video state:', {
        readyState: video.readyState,
        currentTime: video.currentTime,
        duration: video.duration,
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight
      });
      
      // Ensure video is at a good frame (seek to 25% of duration)
      const targetTime = metadataToUse.duration * 0.25;
      console.log('[Blurred Preview] Seeking to time:', targetTime);
      video.currentTime = targetTime;
      
      // Wait for video to seek to the target time
      await new Promise<void>((resolve) => {
        const handleSeeked = () => {
          console.log('[Blurred Preview] Seeked event fired');
          video.removeEventListener('seeked', handleSeeked);
          resolve();
        };
        video.addEventListener('seeked', handleSeeked);
        
        // Fallback timeout in case seeked doesn't fire
        setTimeout(() => {
          console.log('[Blurred Preview] Seek timeout - proceeding anyway');
          video.removeEventListener('seeked', handleSeeked);
          resolve();
        }, 1000);
      });

      console.log('[Blurred Preview] Video seeked to time:', video.currentTime);
      console.log('[Blurred Preview] Video ready state after seek:', video.readyState);

      // Set canvas to 16:9 aspect ratio (1920x1080)
      const targetWidth = 1920;
      const targetHeight = 1080;
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      console.log('[Blurred Preview] Canvas size set to:', targetWidth, 'x', targetHeight);
      
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        console.error('[Blurred Preview] Could not get canvas context');
        return null;
      }

      // Scale video to fit height, center horizontally
      const scale = targetHeight / metadataToUse.height;
      const scaledWidth = metadataToUse.width * scale;
      const scaledHeight = targetHeight;
      const offsetX = (targetWidth - scaledWidth) / 2;

      console.log('[Blurred Preview] Drawing calculations:', {
        scale,
        scaledWidth,
        scaledHeight,
        offsetX
      });

      console.log('[Blurred Preview] Drawing blurred background...');
      // First, draw the blurred background (stretched to fill entire canvas)
      ctx.filter = 'blur(20px)';
      try {
        ctx.drawImage(video, 0, 0, targetWidth, targetHeight);
        console.log('[Blurred Preview] Blurred background drawn successfully');
      } catch (drawError) {
        console.error('[Blurred Preview] Error drawing blurred background:', drawError);
        return null;
      }
      
      console.log('[Blurred Preview] Drawing centered original video...');
      // Reset filter and draw the original video centered
      ctx.filter = 'none';
      try {
        ctx.drawImage(video, offsetX, 0, scaledWidth, scaledHeight);
        console.log('[Blurred Preview] Centered video drawn successfully');
      } catch (drawError) {
        console.error('[Blurred Preview] Error drawing centered video:', drawError);
        return null;
      }

      console.log('[Blurred Preview] Converting canvas to blob...');
      // Convert to blob URL
      return new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (blob && blob.size > 0) {
            const url = URL.createObjectURL(blob);
            console.log('[Blurred Preview] Successfully created blob URL:', url, 'Size:', blob.size, 'bytes');
            resolve(url);
          } else {
            console.error('[Blurred Preview] Failed to create blob or blob is empty. Blob:', blob);
            resolve(null);
          }
        }, 'image/jpeg', 0.9);
      });
    } catch (error) {
      console.error('[Blurred Preview] Error generating preview:', error);
      return null;
    }
  }, [videoMetadata, videoRef, canvasRef]); // Added dependencies for useCallback

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      return;
    }

    // Validate file size (limit to 500MB)
    if (file.size > 500 * 1024 * 1024) {
      setUploadError('File size must be less than 500MB');
      return;
    }

    setSelectedFile(file);
    setUploadError(null);
    
    // Create preview URL
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    setShowCropOverlay(false);

    // Reset YouTube specific states if a local file is selected
    setServerVideoPath(null);
    setYoutubeOriginalFileName(null);
    setIsVideoFromYoutubeSource(false);

    // Generate a new UUID for this upload
    const newVideoId = uuidv4();
    setVideoId(newVideoId);

    // Close any previous EventSource
    if (eventSource) {
      eventSource.close();
      setEventSource(null);
    }

    // Always use full SERVER_BASE_URL for SSE connections
    const sseUrl = `${SERVER_BASE_URL}/api/video-progress/${encodeURIComponent(newVideoId)}`;
    
    console.log('[SSE Debug] Main SSE URL:', sseUrl);
    console.log('[SSE Debug] NODE_ENV:', process.env.NODE_ENV);
    console.log('[SSE Debug] SERVER_BASE_URL:', SERVER_BASE_URL);

    const source = await authedEventSource(sseUrl);
    setProcessingProgress(0);
    setProcessingStep('Waiting for processing to start...');

    console.log('[SSE Debug] Opening connection to:', sseUrl);
    console.log('[SSE Debug] EventSource readyState:', source.readyState);

    // Add timeout to detect stuck connections
    let connectionTimeout: NodeJS.Timeout | null = null;
    connectionTimeout = setTimeout(() => {
      if (source.readyState === EventSource.CONNECTING) {
        console.error('[SSE Debug] Connection timeout - still in CONNECTING state after 10 seconds');
        source.close();
        enqueueSnackbar('Connection timeout. Please try again.', { variant: 'error' });
        setIsProcessing(false);
        setProcessingStep('Connection failed');
      }
    }, 10000); // 10 second timeout

    source.onopen = (event) => {
      if (connectionTimeout) clearTimeout(connectionTimeout); // Clear timeout on successful connection
      console.log('[SSE Debug] Connection opened successfully:', event);
      console.log('[SSE Debug] ReadyState after open:', source.readyState);
      console.log('[SSE Debug] URL:', source.url);
      
      // Debug: Log connection state every 2 seconds
      const debugInterval = setInterval(() => {
        console.log('[SSE Debug] Connection state check - ReadyState:', source.readyState, 'URL:', source.url);
      }, 2000);
      
      // Clean up interval when connection closes
      source.addEventListener('close', () => {
        clearInterval(debugInterval);
      });
    };

    source.onmessage = (event) => {
      console.log('[SSE Debug] Event received:', event.data);
      console.log('[SSE Debug] Event type:', event.type);
      try {
        const data = JSON.parse(event.data);
        console.log('[SSE Debug] Parsed data:', data);

        // Check for horizontal completion - switch to vertical indeterminate progress
        if (data.phase === 'horizontal_complete' || data.status === 'completed' ||
            data.phase === 'vertical_generation' || data.phase === 'vertical_processing' ||
            data.phase === 'vertical_polling' || data.phase === 'vertical_download') {
          console.log('[SSE Debug] Horizontal complete, switching to vertical indeterminate progress');
          setProcessingPhase('vertical');
          setProcessingProgress(-1); // Indeterminate progress bar
          setProcessingStep('Generating vertical video...');
          // Start polling for vertical completion
          startVerticalCompletionPolling();
        } else if (data.percent !== undefined) {
          // Horizontal phase - show determinate progress
          setProcessingPhase('horizontal');
          const newProgress = Math.min(100, Math.round(data.percent));
          console.log(`[SSE Debug] Horizontal progress: ${data.percent}% → Display: ${newProgress}%`);
          setProcessingProgress(newProgress);
          setProcessingStep(data.message || `Processing: ${data.percent.toFixed(1)}%`);
        }
        
        // Handle completion
        if (data.completed) {
          console.log('[SSE Debug] Processing completed');
          setProcessingProgress(100);
          
          if (data.s3Url && data.verticalUrl) {
            // Both processes succeeded
            setProcessedVideoUrl(data.s3Url);
            setVerticalVideoUrl(data.verticalUrl);
            setProcessingStep('Processing complete! Both videos ready.');
            enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
            
            // Update Master Video List with both URLs
            updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
          } else if (data.s3Url && data.retryVertical) {
            // Main video succeeded, vertical failed
            setProcessedVideoUrl(data.s3Url);
            setShowRetryVertical(true);
            setProcessingStep('Video processed. Vertical generation failed - click to retry.');
            enqueueSnackbar('Video processed successfully. Vertical video generation failed - would you like to try again?', { variant: 'warning' });
          } else if (data.s3Url && !data.verticalUrl) {
            // Only main video succeeded
            setProcessedVideoUrl(data.s3Url);
            setProcessingStep('Video processed successfully.');
            enqueueSnackbar('Video processed and uploaded to S3', { variant: 'success' });
          } else if (data.verticalUrl && !data.s3Url) {
            // Only vertical succeeded (unusual case)
            setVerticalVideoUrl(data.verticalUrl);
            setProcessingStep('Vertical video generated. Main upload failed.');
            enqueueSnackbar('Vertical video generated successfully, but main upload failed.', { variant: 'warning' });
          } else if (data.errors) {
            // Both failed
            setProcessingStep('Processing failed for both videos.');
            enqueueSnackbar(`Processing failed: ${data.errors.join(', ')}`, { variant: 'error' });
          } else {
            // Fallback for unclear completion state
            setProcessingStep('Processing complete!');
            enqueueSnackbar('Processing completed', { variant: 'info' });
          }
          
          source.close();
          setTimeout(() => {
            if (!showRetryVertical) {
              resetForm();
            }
          }, 3000);
        }
        
        // Handle old-style completion for backward compatibility
        if (data.status === "completed") {
          console.log('[SSE Debug] Legacy completion format', data);
          setProcessingProgress(100);
          setProcessingStep('Processing complete!');
          
          // Check if this legacy completion has the S3 URLs
          if (data.s3Url && data.verticalUrl) {
            console.log('[SSE Debug] Legacy completion has both URLs, updating sheet');
            setProcessedVideoUrl(data.s3Url);
            setVerticalVideoUrl(data.verticalUrl);
            enqueueSnackbar('Video processed successfully! Both horizontal and vertical videos are ready.', { variant: 'success' });
            
            // Update Master Video List with both URLs
            updateMasterVideoListUrls(data.s3Url, data.verticalUrl);
          } else {
            console.log('[SSE Debug] Legacy completion missing URLs, starting polling:', data);
            enqueueSnackbar(`Video processed. Checking for completion details...`, { variant: 'info' });
            
            // Start polling for completion data
            if (data.videoId) {
              console.log('[SSE Debug] Starting completion polling for missing URLs');
              pollForCompletion(data.videoId);
            }
          }
          
          source.close();
          
          // Don't reset form immediately if we're polling for completion
          if (data.s3Url && data.verticalUrl) {
            setTimeout(() => {
              resetForm();
            }, 2000);
          }
        } else if (data.status === "failed") {
          console.error('SSE reported processing failure:', data.error);
          enqueueSnackbar(`Processing failed: ${data.error || 'Unknown FFmpeg error'}`, { variant: 'error' });
          setIsProcessing(false);
          setProcessingStep('Processing failed');
          source.close();
        }
      } catch (parseError) {
        console.error('[SSE Debug] Failed to parse event data:', parseError, 'Raw data:', event.data);
      }
    };

          source.onerror = (err) => {
        if (connectionTimeout) clearTimeout(connectionTimeout); // Clear timeout on error
        console.error('[SSE Debug] EventSource failed:', err);
        console.error('[SSE Debug] ReadyState on error:', source.readyState);
        console.error('[SSE Debug] Error event details:', {
          type: err.type,
          target: err.target,
          currentTarget: err.currentTarget
        });
        enqueueSnackbar('Connection lost. Checking for completion...', { variant: 'warning' });
        
        // Start polling for completion as fallback
        if (videoId) {
          console.log('[SSE Debug] Starting completion polling due to SSE error');
          pollForCompletion(videoId);
        }
        
        // Don't immediately close on error - let it try to reconnect
        if (source.readyState === EventSource.CLOSED) {
          console.log('[SSE Debug] Connection closed due to error, processing state:', { isProcessing, processingProgress });
          setProcessingStep('Connection lost, checking completion...');
        }
      };

      // Add event listener for when the SSE connection closes
      source.addEventListener('close', () => {
        console.log('[SSE Debug] SSE connection explicitly closed');
      });
  };

  const handleVideoLoaded = useCallback(async () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const metadata: VideoMetadata = {
      width: video.videoWidth,
      height: video.videoHeight,
      duration: video.duration,
      aspectRatio: video.videoWidth / video.videoHeight
    };

    setVideoMetadata(metadata);
    setIsVideoLoaded(true);

    // Initialize crop area to full video
    setCropArea({
      x: 0,
      y: 0,
      width: video.videoWidth,
      height: video.videoHeight
    });

    console.log('Video loaded:', metadata);

    // Check if video is close to 4:3 aspect ratio (within 10% threshold)
    const targetAspectRatio = 4 / 3; // 1.333...
    const aspectDifference = Math.abs(metadata.aspectRatio - targetAspectRatio) / targetAspectRatio;
    const is43AspectRatio = aspectDifference < 0.1; // Within 10%

    if (is43AspectRatio) {
      console.log('[Auto 4:3 Detection] 4:3 video detected automatically:', {
        aspectRatio: metadata.aspectRatio,
        target: targetAspectRatio,
        difference: (aspectDifference * 100).toFixed(1) + '%'
      });
      
      // Set 4:3 state
      setIs43Video(true);
      
      // Wait a bit for video to be fully ready, then generate preview
      setTimeout(async () => {
        const previewUrl = await generateBlurredPreview(metadata);
        if (previewUrl) {
          setBlurredPreviewUrl(previewUrl);
          setShowCropOverlay(true); // Automatically show the preview
          console.log('[Auto 4:3 Detection] Automatically generated and showing blurred preview');
        } else {
          console.error('[Auto 4:3 Detection] Failed to generate preview automatically');
        }
      }, 500); // Small delay to ensure video is ready
    } else {
      console.log('[Auto 4:3 Detection] Not a 4:3 video:', {
        aspectRatio: metadata.aspectRatio,
        target: targetAspectRatio,
        difference: (aspectDifference * 100).toFixed(1) + '%'
      });
      
      // Reset 4:3 state for non-4:3 videos
      setIs43Video(false);
      setIsPillarboxed43(false);
      setIsLetterboxed(false);
      setIsPillarboxed(false);
      setBlurredPreviewUrl(null);
    }
  }, [generateBlurredPreview, videoMetadata]); // Added generateBlurredPreview to dependency array

  const calculateAspectRatioCrop = (newCrop: Partial<CropArea>, metadata: VideoMetadata): CropArea => {
    const targetAspectRatio = 16 / 9;
    let { x = cropArea.x, y = cropArea.y, width = cropArea.width, height = cropArea.height } = newCrop;

    // Ensure crop stays within video bounds
    x = Math.max(0, Math.min(x, metadata.width));
    y = Math.max(0, Math.min(y, metadata.height));

    // Adjust width/height to maintain 16:9 aspect ratio
    if (width && height) {
      const currentRatio = width / height;
      if (currentRatio > targetAspectRatio) {
        // Too wide, adjust height
        height = width / targetAspectRatio;
      } else {
        // Too tall, adjust width
        width = height * targetAspectRatio;
      }
    }

    // Ensure crop doesn't exceed video bounds
    if (x + width > metadata.width) {
      width = metadata.width - x;
      height = width / targetAspectRatio;
    }
    if (y + height > metadata.height) {
      height = metadata.height - y;
      width = height * targetAspectRatio;
    }

    return { x, y, width, height };
  };

  const setFullFrameCrop = () => {
    if (!videoMetadata) {
      enqueueSnackbar('Video metadata not available', { variant: 'warning' });
      return;
    }

    const targetAspect = 16 / 9; // 1.777...
    const videoAspect = videoMetadata.width / videoMetadata.height;

    let cropWidth, cropHeight, cropX, cropY;

    if (Math.abs(videoAspect - targetAspect) < 0.01) {
      // Already 16:9, use full frame
      cropWidth = videoMetadata.width;
      cropHeight = videoMetadata.height;
      cropX = 0;
      cropY = 0;
    } else if (videoAspect > targetAspect) {
      // Video is wider than 16:9, constrain by height
      cropHeight = videoMetadata.height;
      cropWidth = Math.round(cropHeight * targetAspect);
      cropX = Math.round((videoMetadata.width - cropWidth) / 2);
      cropY = 0;
    } else {
      // Video is taller than 16:9, constrain by width
      cropWidth = videoMetadata.width;
      cropHeight = Math.round(cropWidth / targetAspect);
      cropX = 0;
      cropY = Math.round((videoMetadata.height - cropHeight) / 2);
    }

    setCropArea({
      x: cropX,
      y: cropY,
      width: cropWidth,
      height: cropHeight
    });

    setShowCropOverlay(true);
    enqueueSnackbar('Full frame 16:9 crop applied', { variant: 'success' });
  };

  const detectOptimalCrop = async () => {
    // Guard clause: Ensure necessary data is available
    if (!videoRef.current || !videoMetadata || (!selectedFile && !serverVideoPath)) {
      enqueueSnackbar('Video data not ready for crop detection.', { variant: 'warning' });
      return;
    }

    setIsAIDetecting(true);
    try {
      console.log(`[AI Crop Debug] Starting crop detection. Source: ${isVideoFromYoutubeSource ? 'YouTube (server file)' : 'Local file'}`);
      console.log('[AI Crop Debug] Video metadata:', videoMetadata);
      
      const formData = new FormData();
      formData.append('videoWidth', videoMetadata.width.toString());
      formData.append('videoHeight', videoMetadata.height.toString());

      if (serverVideoPath) {
        // Use server file path (works for both YouTube downloads and local uploads)
        console.log('[AI Crop Debug] Using server file path:', serverVideoPath);
        formData.append('videoPath', serverVideoPath); // Send server-relative path
        // Optionally, send original filename if backend needs it for logging/naming, though not strictly needed for crop detection
        if (youtubeOriginalFileName) {
          formData.append('originalFileName', youtubeOriginalFileName);
        }
      } else if (selectedFile) {
        console.log('[AI Crop Debug] Using local file:', selectedFile.name);
        formData.append('video', selectedFile); // Send the actual video file
      } else {
        // This case should ideally be caught by the guard clause at the top
        throw new Error('No valid video source (local file or YouTube path) for crop detection.');
      }

      try {
        console.log('[AI Crop Debug] Sending data to /api/detect-crop-area');
        const response = await fetch(`${SERVER_BASE_URL}/api/detect-crop-area`, {
          method: 'POST',
          body: formData
        });

        console.log('[AI Crop Debug] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[AI Crop Debug] Error response:', errorText);
          throw new Error(`AI crop detection failed: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        console.log('[AI Crop Debug] Success response:', result);
        
        if (result.success && result.cropArea) {
          // Check if this is a 4:3 detection (either full frame or pillarboxed content)
          const isFullFrame = result.cropArea.x === 0 && result.cropArea.y === 0 && 
                             result.cropArea.width === videoMetadata.width && 
                             result.cropArea.height === videoMetadata.height;
          
          const hasSpecialContent = result.message?.includes('Letterboxed') || result.message?.includes('Pillarboxed');
          const isLetterboxed = result.isLetterboxed || result.message?.includes('Letterboxed');
          const isPillarboxed = result.isPillarboxed || result.message?.includes('Pillarboxed');
          const isWideFile = result.isWideFile || result.message?.includes('Wide');
          
          if (isFullFrame) {
            // File-level 4:3 video - show blurred preview
            console.log('[AI Crop Debug] File-level 4:3 content detected - using full frame for blurred background processing');
            setCropArea(result.cropArea);
            setIs43Video(true);
            setIsPillarboxed43(false);
            setIsLetterboxed(false);
            setIsPillarboxed(false);
            setIsWideFile(false);
            
            // Generate blurred background preview
            const previewUrl = await generateBlurredPreview(videoMetadata);
            if (previewUrl) {
              setBlurredPreviewUrl(previewUrl);
              console.log('[Blurred Preview] Generated blurred background preview for file-level 4:3');
            }
          } else if (isWideFile) {
            // Wide video file (wider than 16:9) - show red crop overlay for 16:9 crop
            console.log('[AI Crop Debug] Wide video file detected - showing centered 16:9 crop area');
            setCropArea(result.cropArea);
            setIs43Video(false);
            setIsPillarboxed43(false);
            setIsLetterboxed(false);
            setIsPillarboxed(false);
            setIsWideFile(true);
            setBlurredPreviewUrl(null);
          } else if (hasSpecialContent) {
            // Letterboxed or Pillarboxed content - show crop overlay
            console.log('[AI Crop Debug] Special content detected:', {
              type: result.contentType,
              isLetterboxed,
              isPillarboxed,
              message: result.message
            });
            setCropArea(result.cropArea); // Use exact coordinates from server
            setIs43Video(false);
            setIsPillarboxed43(isPillarboxed && !isLetterboxed); // Only pillarboxed gets blurred background, NOT letterboxed
            setIsLetterboxed(isLetterboxed);
            setIsPillarboxed(isPillarboxed);
            setBlurredPreviewUrl(null);
          } else {
            // Regular 16:9 content - apply aspect ratio adjustment
            console.log('[AI Crop Debug] Regular 16:9 content detected - applying aspect ratio adjustment');
            const aiCrop = calculateAspectRatioCrop(result.cropArea, videoMetadata);
            setCropArea(aiCrop);
            setIs43Video(false);
            setIsPillarboxed43(false);
            setIsLetterboxed(false);
            setIsPillarboxed(false);
            setBlurredPreviewUrl(null);
          }
          
          setShowCropOverlay(true);
        } else {
          throw new Error(result.message || 'Failed to detect crop area');
        }
      } catch (error) {
        console.error('[AI Crop Debug] Network/API error:', error);
        enqueueSnackbar(`AI crop detection failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'warning' });
        
        // Fallback: suggest center crop
        const centerCrop = {
          x: videoMetadata.width * 0.1,
          y: videoMetadata.height * 0.1,
          width: videoMetadata.width * 0.8,
          height: videoMetadata.height * 0.8
        };
        const fallbackCrop = calculateAspectRatioCrop(centerCrop, videoMetadata);
        setCropArea(fallbackCrop);
        setIs43Video(false);
        setIsPillarboxed43(false);
        setIsLetterboxed(false);
        setIsPillarboxed(false);
        setBlurredPreviewUrl(null);
        setShowCropOverlay(true);
      }

    } catch (error) {
      console.error('[AI Crop Debug] General error:', error);
      enqueueSnackbar(`Failed to analyze video: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
    } finally {
      setIsAIDetecting(false);
    }
  };

  const handleCropChange = (field: keyof CropArea, value: number) => {
    if (!videoMetadata) return;

    const newCrop = { ...cropArea, [field]: value };
    const adjustedCrop = calculateAspectRatioCrop(newCrop, videoMetadata);
    setCropArea(adjustedCrop);
  };

  const processVideo = async () => {
    // Guard clause: Ensure necessary data for processing is available
    if (!videoMetadata || !videoId || (!selectedFile && !serverVideoPath)) {
      enqueueSnackbar('No valid video source selected for processing', { variant: 'error' });
      return;
    }

    setIsProcessing(true);
    setProcessingProgress(0);
    setProcessingStep(isVideoFromYoutubeSource ? 'Preparing downloaded video...' : 'Uploading video...');
    setProcessingPhase('horizontal'); // Start with horizontal processing

    // Ensure SSE connection is set up for this videoId
    // If a connection already exists, this will be a no-op as we reuse it
    // If no connection exists, we create one now
    if (!eventSource || !eventSourceRef.current) {
      console.log('[Process Video] Setting up SSE connection for videoId:', videoId);
      const processingSseUrl = `${SERVER_BASE_URL}/api/video-progress/${encodeURIComponent(videoId)}`;

      const processingSSE = await authedEventSource(processingSseUrl);
      setEventSource(processingSSE);
      eventSourceRef.current = processingSSE;

      // Safety net: Start polling after 90s if still processing (in case SSE drops silently)
      const safetyNetTimer = setTimeout(() => {
        if (isProcessingRef.current && videoId) {
          console.log('[Safety Net] 90s elapsed, starting polling as backup (processVideo flow)');
          pollForCompletion(videoId);
        }
      }, 90000);

      processingSSE.onmessage = (event) => {
        console.log('[SSE Debug] Processing Event:', event.data);
        try {
          const data = JSON.parse(event.data);

          // Handle FINAL completion first (phase='completed' with verticalUrl)
          // This is the true completion event from the server
          if ((data.phase === 'completed' || data.phase === 'complete') && data.completed === true && data.verticalUrl) {
            console.log('[SSE Debug] FINAL completion - vertical video ready:', data.verticalUrl);

            // Stop any polling
            if (verticalPollRef.current) {
              clearTimeout(verticalPollRef.current);
              verticalPollRef.current = null;
            }
            verticalPollActiveRef.current = false;

            setProcessingProgress(100);
            setProcessingStep('Complete!');
            setIsProcessing(false);

            if (data.verticalUrl) setVerticalVideoUrl(data.verticalUrl);

            enqueueSnackbar('Video processing complete!', { variant: 'success' });

            // Update Master Video List with both URLs
            // Use processedVideoUrl (horizontal) which was set earlier, and the new verticalUrl
            if (processedVideoUrl && data.verticalUrl) {
              console.log('[SSE Debug] Updating Master Video List with URLs:', { horizontal: processedVideoUrl, vertical: data.verticalUrl });
              updateMasterVideoListUrls(processedVideoUrl, data.verticalUrl, data.extractedMetadata, true);
            } else if (data.s3Url && data.verticalUrl) {
              // Fallback: use s3Url from the completion message if available
              console.log('[SSE Debug] Updating Master Video List with URLs (from completion):', { horizontal: data.s3Url, vertical: data.verticalUrl });
              updateMasterVideoListUrls(data.s3Url, data.verticalUrl, data.extractedMetadata, true);
            } else {
              console.log('[SSE Debug] Missing URLs for sheet update:', { processedVideoUrl, s3Url: data.s3Url, verticalUrl: data.verticalUrl });
            }

            // Close SSE connection to prevent reconnect loop
            processingSSE.close();
            if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === processingSSE ? null : prev);

            // Navigate after completion - either to return URL or video manager
            navigateAfterCompletion(data.verticalUrl);
            return;
          }

          // Capture horizontal URL when it becomes available
          if (data.s3Url && !processedVideoUrl) {
            console.log('[SSE Debug] Capturing horizontal URL:', data.s3Url);
            setProcessedVideoUrl(data.s3Url);
          }

          // Check for horizontal completion - switch to vertical indeterminate progress
          // Note: data.status === 'completed' with percent=100 is horizontal completion, NOT final completion
          if (data.phase === 'horizontal_complete' ||
              data.phase === 'vertical_generation' || data.phase === 'vertical_processing' ||
              data.phase === 'vertical_polling' || data.phase === 'vertical_download' ||
              data.phase === 'video_manager_update') {
            console.log('[SSE Debug] Horizontal complete, switching to vertical indeterminate progress');
            setProcessingPhase('vertical');
            setProcessingProgress(-1); // Indeterminate progress bar
            setProcessingStep(data.message || 'Generating vertical video...');
            // For direct uploads (no jobId), we rely on SSE for completion, not polling
          } else if (data.percent !== undefined && data.phase !== 'completed') {
            // Horizontal phase - show determinate progress (but not if phase is 'completed')
            setProcessingPhase('horizontal');
            const newProgress = Math.min(100, Math.round(data.percent));
            console.log(`[SSE Debug] Horizontal progress: ${data.percent}% → Display: ${newProgress}%`);
            setProcessingProgress(newProgress);
            setProcessingStep(data.message || 'Processing...');
          }

          // Handle legacy completion format (data.phase === 'complete' with results)
          if (data.phase === 'complete' && data.results) {
            console.log('[SSE Debug] Processing complete (legacy format):', data.results);

            // Stop vertical progress simulation
            if (verticalProgressIntervalRef.current) {
              clearInterval(verticalProgressIntervalRef.current);
              verticalProgressIntervalRef.current = null;
            }
            setProcessingProgress(100);
            setVerticalProgressSimulation(100);

            const { horizontalUrl, verticalUrl } = data.results;
            if (horizontalUrl) setProcessedVideoUrl(horizontalUrl);
            if (verticalUrl) setVerticalVideoUrl(verticalUrl);

            enqueueSnackbar('Video processing complete!', { variant: 'success' });
            setIsProcessing(false);

            // Update Master Video List with URLs
            updateMasterVideoListUrls(horizontalUrl, verticalUrl);

            processingSSE.close();
            if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === processingSSE ? null : prev);
          }

          // Handle errors
          if (data.phase === 'error' && data.error) {
            console.error('Processing SSE Error:', data.error);
            enqueueSnackbar(`Processing Error: ${data.error}`, { variant: 'error' });

            if (verticalProgressIntervalRef.current) {
              clearInterval(verticalProgressIntervalRef.current);
              verticalProgressIntervalRef.current = null;
            }

            setIsProcessing(false);
            processingSSE.close();
            if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
            setEventSource(prev => prev === processingSSE ? null : prev);
          }
        } catch (error) {
          console.error('Error parsing processing SSE data:', error);
        }
      };

      processingSSE.onerror = (event) => {
        console.error('Processing SSE connection error:', event);
        clearTimeout(safetyNetTimer);
        if (processingSSE.readyState === EventSource.CLOSED) {
          setIsProcessing(false);
          enqueueSnackbar('Processing connection failed', { variant: 'error' });

          if (verticalProgressIntervalRef.current) {
            clearInterval(verticalProgressIntervalRef.current);
            verticalProgressIntervalRef.current = null;
          }

          if (eventSourceRef.current === processingSSE) eventSourceRef.current = null;
          setEventSource(prev => prev === processingSSE ? null : prev);
        }
      };
    } else {
      console.log('[Process Video] Using existing SSE connection');
    }

    try {
      const formData = new FormData();
      formData.append('cropArea', JSON.stringify(cropArea));
      formData.append('targetWidth', '1920');
      formData.append('targetHeight', '1080');
      formData.append('videoId', videoId);

      // Add song/artist metadata if available (from YouTube download)
      if (songFromVideoManager) {
        formData.append('song', songFromVideoManager);
        console.log('[Process Video] Including song metadata:', songFromVideoManager);
      }
      if (artistFromVideoManager) {
        formData.append('artist', artistFromVideoManager);
        console.log('[Process Video] Including artist metadata:', artistFromVideoManager);
      }
      if (videoManagerRowIndex !== null && videoManagerRowIndex !== undefined) {
        formData.append('sheetRowIndex', videoManagerRowIndex.toString());
        console.log('[Process Video] Including sheetRowIndex:', videoManagerRowIndex);
      }

      if (serverVideoPath) {
        // Use server file path (works for both YouTube downloads and local uploads)
        console.log('[Process Video] Using server file path:', serverVideoPath);
        formData.append('videoPath', serverVideoPath);
        if (youtubeOriginalFileName) {
          formData.append('originalFileName', youtubeOriginalFileName);
        }
      } else if (selectedFile) {
        console.log('[Process Video] Using local file:', selectedFile.name);
        formData.append('video', selectedFile);
      } else {
        throw new Error('No valid video source (local file or YouTube path) for processing.');
      }

      // If we have a server video path, we skip the XHR upload progress part as the file is already on the server.
      // We will directly initiate the processing via a fetch call that the backend SSE will then track.
      if (serverVideoPath) {
        setProcessingStep('Initiating processing for downloaded video...');
        const processResponse = await fetch(`${SERVER_BASE_URL}/api/process-video`, {
          method: 'POST',
          body: formData,
        });

        if (!processResponse.ok) {
          const errorBody = await processResponse.text();
          throw new Error(`Processing initialization failed: ${processResponse.status} - ${errorBody}`);
        }

        const result = await processResponse.json();
        if (result.success) {
          setProcessingStep('Processing video');
          // SSE connection should already be open from handleFileSelect or downloadFromYoutube
          // and will receive detailed progress updates from the backend.
        } else {
          throw new Error(result.message || 'Processing initialization failed on server');
        }

      } else {
        // For local files, use XMLHttpRequest for upload progress as before
        const xhr = new XMLHttpRequest();
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const uploadPercent = Math.round((event.loaded / event.total) * 30); 
            setProcessingProgress(uploadPercent);
            if (uploadPercent === 30) {
              setProcessingStep('Upload complete. Starting processing...');
            } else {
              setProcessingStep(`Uploading: ${uploadPercent}%`);
            }
          }
        };

        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const processResponseData = JSON.parse(xhr.responseText);
              if (processResponseData.success) {
                setProcessingStep('Processing video');
                // SSE connection is already open and will handle further progress
              } else {
                throw new Error(processResponseData.message || 'Processing initialization failed after upload');
              }
            } catch (e) {
              console.error('Error parsing process response or setting up SSE:', e);
              enqueueSnackbar(`Error during processing setup: ${e instanceof Error ? e.message : 'Unknown error'}`, { variant: 'error' });
              setIsProcessing(false);
              setProcessingStep('Error setting up processing');
            }
          } else {
            let errorMsg = `Processing request failed: ${xhr.status}`;
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              if (errorResponse && errorResponse.details) {
                errorMsg += ` - ${errorResponse.details}`;
              } else if (errorResponse && errorResponse.error) {
                errorMsg += ` - ${errorResponse.error}`;
              }
            } catch (e) { /* ignore if response is not json */ }
            enqueueSnackbar(errorMsg, { variant: 'error' });
            setIsProcessing(false);
            setProcessingStep('Processing request error');
          }
        };

        xhr.onerror = () => {
          enqueueSnackbar('Network error during video processing request.', { variant: 'error' });
          setIsProcessing(false);
          setProcessingStep('Network error');
        };

        xhr.open('POST', `${SERVER_BASE_URL}/api/process-video`, true);
        xhr.send(formData);
      }

    } catch (error) {
      console.error('Video processing error (outer try-catch):', error);
      enqueueSnackbar(`Processing failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
      setIsProcessing(false);
      setProcessingProgress(0);
      setProcessingStep('Critical error');
    }
  };

  // Cleanup video URL on unmount
  const currentVideoUrl = videoUrl;
  const currentBlurredPreviewUrl = blurredPreviewUrl;
  useEffect(() => {
    return () => {
      if (currentVideoUrl) {
        URL.revokeObjectURL(currentVideoUrl);
      }
      if (currentBlurredPreviewUrl) {
        URL.revokeObjectURL(currentBlurredPreviewUrl);
      }
    };
  }, [currentVideoUrl, currentBlurredPreviewUrl]); // Corrected dependencies

  // Update progress display when vertical simulation changes
  useEffect(() => {
    if (processingPhase === 'vertical') {
      setProcessingProgress(verticalProgressSimulation);
    }
  }, [verticalProgressSimulation, processingPhase]);

  // Keep isProcessingRef in sync with isProcessing state for deferred callbacks.
  useEffect(() => {
    isProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Cleanup EventSource and intervals on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        console.log('[SSE Debug] Cleaning up EventSource on unmount');
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (verticalProgressIntervalRef.current) {
        clearInterval(verticalProgressIntervalRef.current);
        verticalProgressIntervalRef.current = null;
      }
      // Also clear the three completion pollers so they don't keep firing
      // (and calling setState) after the component unmounts.
      if (completionPollRef.current) {
        clearInterval(completionPollRef.current);
        completionPollRef.current = null;
      }
      if (downloadPollRef.current) {
        clearInterval(downloadPollRef.current);
        downloadPollRef.current = null;
      }
      if (verticalPollRef.current) {
        clearTimeout(verticalPollRef.current);
        verticalPollRef.current = null;
      }
    };
  }, []);

  // Effect to update playerRect when video loads or window resizes
  useEffect(() => {
    const updatePlayerRect = () => {
      if (videoRef.current && isVideoLoaded) {
        const rect = videoRef.current.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          setPlayerRect({ width: rect.width, height: rect.height });
        }
      }
    };

    updatePlayerRect(); // Initial call

    window.addEventListener('resize', updatePlayerRect);
    return () => {
      window.removeEventListener('resize', updatePlayerRect);
    };
  }, [isVideoLoaded, videoUrl]); // videoUrl ensures re-calc if video source changes

  const cropOverlayStyleProps = useMemo(() => {
    if (!videoMetadata || !cropArea || !playerRect || playerRect.width === 0 || playerRect.height === 0) {
      return { top: '0%', left: '0%', width: '0%', height: '0%' };
    }

    const playerWidth = playerRect.width;
    const playerHeight = playerRect.height;

    const videoNaturalWidth = videoMetadata.width;
    const videoNaturalHeight = videoMetadata.height;

    const playerContainerAspectRatio = playerWidth / playerHeight;
    const videoContentAspectRatio = videoNaturalWidth / videoNaturalHeight;

    let actualDisplayedContentWidth, actualDisplayedContentHeight, contentOffsetX_px, contentOffsetY_px;

    if (videoContentAspectRatio > playerContainerAspectRatio) {
      // Video content is wider than player container (letterboxed)
      actualDisplayedContentWidth = playerWidth;
      actualDisplayedContentHeight = playerWidth / videoContentAspectRatio;
      contentOffsetX_px = 0;
      contentOffsetY_px = (playerHeight - actualDisplayedContentHeight) / 2;
    } else {
      // Video content is narrower or same aspect as player container (pillarboxed or fills)
      actualDisplayedContentHeight = playerHeight;
      actualDisplayedContentWidth = playerHeight * videoContentAspectRatio;
      contentOffsetY_px = 0;
      contentOffsetX_px = (playerWidth - actualDisplayedContentWidth) / 2;
    }

    // Ensure no division by zero if natural width/height are zero (though unlikely with videoMetadata check)
    if (videoNaturalWidth === 0 || videoNaturalHeight === 0) {
        return { top: '0%', left: '0%', width: '0%', height: '0%' };
    }
    
    const scale = actualDisplayedContentWidth / videoNaturalWidth;

    const cropOnScreenX_px = contentOffsetX_px + (cropArea.x * scale);
    const cropOnScreenY_px = contentOffsetY_px + (cropArea.y * scale);
    const cropOnScreenWidth_px = cropArea.width * scale;
    const cropOnScreenHeight_px = cropArea.height * scale;

    return {
      top: `${(cropOnScreenY_px / playerHeight) * 100}%`,
      left: `${(cropOnScreenX_px / playerWidth) * 100}%`,
      width: `${(cropOnScreenWidth_px / playerWidth) * 100}%`,
      height: `${(cropOnScreenHeight_px / playerHeight) * 100}%`,
    };
  }, [videoMetadata, cropArea, playerRect]);

  // Add mouse event handlers for corner dragging and moving
  const handleCornerMouseDown = (corner: 'tl' | 'tr' | 'bl' | 'br', event: React.MouseEvent) => {
    if (!videoRef.current || !videoMetadata) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const videoRect = videoRef.current.getBoundingClientRect();
    const startX = event.clientX - videoRect.left;
    const startY = event.clientY - videoRect.top;
    
    setDragCorner(corner);
    setDragStart({
      x: startX,
      y: startY,
      cropArea: { ...cropArea }
    });
  };

  const handleCropBoxMouseDown = (event: React.MouseEvent) => {
    if (!videoRef.current || !videoMetadata) return;
    
    event.preventDefault();
    event.stopPropagation();
    
    const videoRect = videoRef.current.getBoundingClientRect();
    const startX = event.clientX - videoRect.left;
    const startY = event.clientY - videoRect.top;
    
    setDragCorner('move');
    setDragStart({
      x: startX,
      y: startY,
      cropArea: { ...cropArea }
    });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!dragStart || !videoRef.current || !videoMetadata || !dragCorner) return;
    
    const videoRect = videoRef.current.getBoundingClientRect();
    const currentX = event.clientX - videoRect.left;
    const currentY = event.clientY - videoRect.top;
    
    // Convert screen coordinates to video coordinates
    const scaleX = videoMetadata.width / videoRect.width;
    const scaleY = videoMetadata.height / videoRect.height;
    
    const deltaX = (currentX - dragStart.x) * scaleX;
    const deltaY = (currentY - dragStart.y) * scaleY;
    
    const originalCrop = dragStart.cropArea;
    let newCrop = { ...originalCrop };
    
    // Update crop area based on which corner is being dragged or if moving
    // Maintain 16:9 aspect ratio (1.78)
    const aspectRatio = 16 / 9;
    
    switch (dragCorner) {
      case 'move': // Moving the entire crop box
        newCrop.x = originalCrop.x + deltaX;
        newCrop.y = originalCrop.y + deltaY;
        break;
      case 'tl': // Top-left corner
        // Use width as the primary dimension for aspect ratio
        const newWidthTL = originalCrop.width - deltaX;
        const newHeightTL = newWidthTL / aspectRatio;
        newCrop.x = originalCrop.x + originalCrop.width - newWidthTL;
        newCrop.y = originalCrop.y + originalCrop.height - newHeightTL;
        newCrop.width = newWidthTL;
        newCrop.height = newHeightTL;
        break;
      case 'tr': // Top-right corner
        // Use width as the primary dimension for aspect ratio
        const newWidthTR = originalCrop.width + deltaX;
        const newHeightTR = newWidthTR / aspectRatio;
        newCrop.y = originalCrop.y + originalCrop.height - newHeightTR;
        newCrop.width = newWidthTR;
        newCrop.height = newHeightTR;
        break;
      case 'bl': // Bottom-left corner
        // Use width as the primary dimension for aspect ratio
        const newWidthBL = originalCrop.width - deltaX;
        const newHeightBL = newWidthBL / aspectRatio;
        newCrop.x = originalCrop.x + originalCrop.width - newWidthBL;
        newCrop.width = newWidthBL;
        newCrop.height = newHeightBL;
        break;
      case 'br': // Bottom-right corner
        // Use width as the primary dimension for aspect ratio
        const newWidthBR = originalCrop.width + deltaX;
        const newHeightBR = newWidthBR / aspectRatio;
        newCrop.width = newWidthBR;
        newCrop.height = newHeightBR;
        break;
    }
    
    // Ensure crop area stays within video bounds and has minimum size
    newCrop.x = Math.max(0, newCrop.x);
    newCrop.y = Math.max(0, newCrop.y);
    newCrop.width = Math.max(50, Math.min(newCrop.width, videoMetadata.width - newCrop.x));
    newCrop.height = Math.max(50, Math.min(newCrop.height, videoMetadata.height - newCrop.y));
    
    setCropArea(newCrop);
  };

  const handleMouseUp = () => {
    setDragCorner(null);
    setDragStart(null);
  };

  // Add useEffect to handle global mouse events during dragging
  useEffect(() => {
    if (!dragStart) return;
    
    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (!dragStart || !videoRef.current || !videoMetadata || !dragCorner) return;
      
      const videoRect = videoRef.current.getBoundingClientRect();
      const currentX = event.clientX - videoRect.left;
      const currentY = event.clientY - videoRect.top;
      
      // Convert screen coordinates to video coordinates
      const scaleX = videoMetadata.width / videoRect.width;
      const scaleY = videoMetadata.height / videoRect.height;
      
      const deltaX = (currentX - dragStart.x) * scaleX;
      const deltaY = (currentY - dragStart.y) * scaleY;
      
      const originalCrop = dragStart.cropArea;
      let newCrop = { ...originalCrop };
      
      // Update crop area based on which corner is being dragged or if moving
      // Maintain 16:9 aspect ratio (1.78)
      const aspectRatio = 16 / 9;
      
      switch (dragCorner) {
        case 'move': // Moving the entire crop box
          newCrop.x = originalCrop.x + deltaX;
          newCrop.y = originalCrop.y + deltaY;
          break;
        case 'tl': // Top-left corner
          // Use width as the primary dimension for aspect ratio
          const newWidthTL = originalCrop.width - deltaX;
          const newHeightTL = newWidthTL / aspectRatio;
          newCrop.x = originalCrop.x + originalCrop.width - newWidthTL;
          newCrop.y = originalCrop.y + originalCrop.height - newHeightTL;
          newCrop.width = newWidthTL;
          newCrop.height = newHeightTL;
          break;
        case 'tr': // Top-right corner
          // Use width as the primary dimension for aspect ratio
          const newWidthTR = originalCrop.width + deltaX;
          const newHeightTR = newWidthTR / aspectRatio;
          newCrop.y = originalCrop.y + originalCrop.height - newHeightTR;
          newCrop.width = newWidthTR;
          newCrop.height = newHeightTR;
          break;
        case 'bl': // Bottom-left corner
          // Use width as the primary dimension for aspect ratio
          const newWidthBL = originalCrop.width - deltaX;
          const newHeightBL = newWidthBL / aspectRatio;
          newCrop.x = originalCrop.x + originalCrop.width - newWidthBL;
          newCrop.width = newWidthBL;
          newCrop.height = newHeightBL;
          break;
        case 'br': // Bottom-right corner
          // Use width as the primary dimension for aspect ratio
          const newWidthBR = originalCrop.width + deltaX;
          const newHeightBR = newWidthBR / aspectRatio;
          newCrop.width = newWidthBR;
          newCrop.height = newHeightBR;
          break;
      }
      
      // Ensure crop area stays within video bounds and has minimum size
      newCrop.x = Math.max(0, newCrop.x);
      newCrop.y = Math.max(0, newCrop.y);
      newCrop.width = Math.max(50, Math.min(newCrop.width, videoMetadata.width - newCrop.x));
      newCrop.height = Math.max(50, Math.min(newCrop.height, videoMetadata.height - newCrop.y));
      
      setCropArea(newCrop);
    };
    
    const handleGlobalMouseUp = () => {
      setDragCorner(null);
      setDragStart(null);
    };
    
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [dragStart, dragCorner, videoMetadata]);

  // Add retry function for vertical video generation
  const retryVerticalGeneration = async () => {
    if (!processedVideoUrl || !selectedFile) {
      enqueueSnackbar('Cannot retry - original video or file not available', { variant: 'error' });
      return;
    }

    setShowRetryVertical(false);
    setIsProcessing(true);
    setProcessingProgress(85);
    setProcessingStep('Retrying vertical video generation...');

    try {
      // Call backend endpoint to retry vertical generation using the already processed S3 URL
      const response = await fetch(`${SERVER_BASE_URL}/api/generate-vertical`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          processedVideoUrl,
          originalFilename: selectedFile.name,
          videoId: videoId || 'retry-' + Date.now()
        }),
      });

      if (!response.ok) {
        throw new Error(`Retry failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.verticalUrl) {
        setVerticalVideoUrl(result.verticalUrl);
        setProcessingProgress(100);
        setProcessingStep('Vertical video generated successfully!');
        enqueueSnackbar('Vertical video generated successfully!', { variant: 'success' });
        
        setTimeout(() => {
          resetForm();
        }, 3000);
      } else {
        throw new Error(result.error || 'Retry failed with unknown error');
      }
    } catch (error) {
      console.error('Retry vertical generation error:', error);
      enqueueSnackbar(`Retry failed: ${error instanceof Error ? error.message : 'Unknown error'}`, { variant: 'error' });
      setShowRetryVertical(true); // Show retry option again
      setProcessingStep('Vertical generation retry failed - click to try again');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ 
      mt: 0,
      mx: '2.5%',
      width: '95vw',
      maxWidth: '95vw',
      boxSizing: 'border-box',
      overflow: 'hidden',
      position: 'relative',
      left: '50%',
      transform: 'translateX(-50%)',
      padding: '0 10%'
    }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1} mb={2}>
        <Button
          variant="outlined"
          onClick={() => navigate('/import-manager')}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
            backgroundColor: '#ffffff',
            borderColor: '#000000',
            color: '#000000',
            '&:hover': {
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Import Manager
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/video-manager')}
          size="small"
          sx={{
            height: '40px',
            padding: '4px 16px',
            backgroundColor: '#ffffff',
            borderColor: '#000000',
            color: '#000000',
            '&:hover': {
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Video Manager
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Upload Section - Only show when accessed directly (manual upload mode) */}
        {isManualUploadMode && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoFileIcon /> Video Upload
              </Typography>

              {/* Refactored to a horizontal layout */}
              <Box sx={{
                mb: 3,
                p: 3,
                border: '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex', // Ensures horizontal layout
                alignItems: 'center', // Aligns items vertically in the center
                gap: 2 // Adds space between elements
              }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="video/*"
                  style={{ display: 'none' }}
                />
                <Button
                  variant="contained"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isProcessing || isDownloadingFromYoutube || isInitializingDownload}
                  sx={{
                    height: '56px',
                    flexShrink: 0,
                    backgroundColor: '#000000',
                    color: themeColor,
                    '&:hover': {
                      backgroundColor: '#333333',
                    },
                  }}
                >
                  Select Video File
                </Button>

                <Typography variant="body1" sx={{ mx: 1, fontWeight: 'bold' }}>
                  OR
                </Typography>

                {/* YouTube Download Section - now part of the flex layout */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="YouTube URL"
                    placeholder="https://www.youtube.com/watch?v=..."
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    disabled={isProcessing || isDownloadingFromYoutube || isInitializingDownload}
                    size="medium" // Standard size for TextField
                  />
                  <Button
                    variant="contained"
                    color="primary" // Matches the Select Video File button
                    onClick={() => downloadFromYoutube(youtubeUrl)}
                    disabled={isProcessing || isDownloadingFromYoutube || isInitializingDownload || !youtubeUrl.trim()}
                    sx={{ height: '56px', flexShrink: 0 }} // Fixed height, prevents shrinking
                  >
                    {(isDownloadingFromYoutube || isInitializingDownload) ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      'Select Youtube Video' // Corrected button text
                    )}
                  </Button>
                </Box>
              </Box>

              {/* Show initializing spinner while fetching metadata */}
              {isInitializingDownload && !isDownloadingFromYoutube && (
                <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Preparing download...
                  </Typography>
                </Box>
              )}

              {/* Moved progress and selected file name below the input group */}
              {isDownloadingFromYoutube && (
                <Box sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    {downloadStep || 'Downloading from YouTube...'}
                  </Typography>
                </Box>
              )}

              {/* Show retry button when download failed */}
              {downloadFailed && !isDownloadingFromYoutube && (
                <Box sx={{ mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="error">
                    {downloadStep || 'Download failed. This may be a temporary issue.'}
                  </Alert>
                  <Alert severity="info">
                    YouTube sometimes blocks video downloads. Hit retry to attempt a new download.
                  </Alert>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      if (youtubeUrl) {
                        downloadFromYoutube(youtubeUrl);
                      }
                    }}
                    disabled={!youtubeUrl}
                  >
                    Retry Download
                  </Button>
                </Box>
              )}

              {selectedFile && !isDownloadingFromYoutube && ( // Only show if not YT downloading
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Selected: {selectedFile.name}
                </Typography>
              )}

              {uploadError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {uploadError}
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Download Progress Section - Shows when coming from upload icon with YouTube URL */}
        {!isManualUploadMode && (isInitializingDownload || isDownloadingFromYoutube || downloadFailed) && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoFileIcon /> {downloadFailed ? 'Download Failed' : 'Downloading Video'}
              </Typography>

              {isInitializingDownload && !isDownloadingFromYoutube && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Preparing download...
                  </Typography>
                </Box>
              )}

              {isDownloadingFromYoutube && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    {downloadStep || 'Downloading from YouTube...'}
                  </Typography>
                </Box>
              )}

              {downloadFailed && !isDownloadingFromYoutube && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="error">
                    {downloadStep || 'Download failed. This may be a temporary issue with the YouTube downloader service.'}
                  </Alert>
                  <Alert severity="info">
                    YouTube sometimes blocks video downloads. Hit retry to attempt a new download.
                  </Alert>
                  <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={() => {
                      // Retry the download using the stored YouTube URL
                      if (youtubeUrl) {
                        downloadFromYoutube(youtubeUrl);
                      }
                    }}
                    disabled={!youtubeUrl}
                  >
                    Retry Download
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Video Preview and Crop Section */}
        {videoUrl && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CropIcon /> Video Preview & Crop
              </Typography>

              {/* Video Player */}
              <Box sx={{ position: 'relative', mb: 3 }}>
                {is43Video && showCropOverlay && blurredPreviewUrl ? (
                  // Show blurred background preview for 4:3 videos (replaces video player)
                  <Box
                    sx={{
                      width: '100%',
                      aspectRatio: '16 / 9', // Lock to 16:9 aspect ratio
                      position: 'relative',
                      backgroundColor: '#000',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      border: '3px solid #4caf50',
                      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)'
                    }}
                  >
                    <Box
                      component="img"
                      src={blurredPreviewUrl}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover', // Fill the 16:9 container completely
                        objectPosition: 'center'
                      }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        bottom: '5%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#4caf50',
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}
                    >
                      4:3 Preview - Blurred Background Style
                    </Typography>
                  </Box>
                ) : (
                  // Show regular video player
                  <Box sx={{ /* New container for 16:9 aspect ratio */
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%', /* 16:9 Aspect Ratio */
                    backgroundColor: '#000', /* Background for letter/pillarboxing */
                    borderRadius: '4px', /* Optional: match other styling */
                    overflow: 'hidden' /* Optional: ensure children don't overflow */
                  }}>
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      controls
                      crossOrigin="anonymous"
                      onLoadedMetadata={handleVideoLoaded}
                      onMouseMove={handleMouseMove}
                      onMouseUp={handleMouseUp}
                      style={{ /* Video fills the 16:9 container */
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain', /* Fit video within container, letter/pillarboxing as needed */
                        backgroundColor: '#000', /* Fallback, though parent has it */
                      }}
                    />
                    
                    {/* Enhanced Crop Overlay with Draggable Corners */}
                    {showCropOverlay && videoMetadata && (isLetterboxed || isPillarboxed43 || isWideFile || (!is43Video && !isPillarboxed43)) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          pointerEvents: 'none',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: cropOverlayStyleProps.top,
                            left: cropOverlayStyleProps.left,
                            width: cropOverlayStyleProps.width,
                            height: cropOverlayStyleProps.height,
                            border: isLetterboxed ? '3px solid #ff4444' : isPillarboxed ? '3px solid #ff9800' : isPillarboxed43 ? '3px solid #4caf50' : isWideFile ? '3px solid #ff4444' : '2px solid #ff4444',
                            backgroundColor: isLetterboxed ? 'rgba(255, 68, 68, 0.1)' : isPillarboxed ? 'rgba(255, 152, 0, 0.1)' : isPillarboxed43 ? 'rgba(76, 175, 80, 0.1)' : isWideFile ? 'rgba(255, 68, 68, 0.1)' : 'rgba(255, 68, 68, 0.1)',
                            boxSizing: 'border-box'
                          }
                        }}
                      >
                        {/* Draggable crop area - click and drag to move */}
                        {(isLetterboxed || isPillarboxed43 || isWideFile || (!is43Video && !isPillarboxed43)) && (
                          <Box
                            onMouseDown={handleCropBoxMouseDown}
                            sx={{
                              position: 'absolute',
                              top: cropOverlayStyleProps.top,
                              left: cropOverlayStyleProps.left,
                              width: cropOverlayStyleProps.width,
                              height: cropOverlayStyleProps.height,
                              cursor: 'move',
                              pointerEvents: 'auto',
                              zIndex: 5,
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.05)'
                              }
                            }}
                          />
                        )}
                        
                        {/* Corner Handles */}
                        {(isLetterboxed || isPillarboxed43 || isWideFile || (!is43Video && !isPillarboxed43)) && (
                          <>
                            {/* Top-left corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('tl', e)}
                              sx={{
                                position: 'absolute',
                                top: cropOverlayStyleProps.top,
                                left: cropOverlayStyleProps.left,
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'nw-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                            
                            {/* Top-right corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('tr', e)}
                              sx={{
                                position: 'absolute',
                                top: cropOverlayStyleProps.top, // Uses the top of the main crop box
                                left: `calc(${cropOverlayStyleProps.left} + ${cropOverlayStyleProps.width})`, // Uses left + width of main crop box
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'ne-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                            
                            {/* Bottom-left corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('bl', e)}
                              sx={{
                                position: 'absolute',
                                top: `calc(${cropOverlayStyleProps.top} + ${cropOverlayStyleProps.height})`, // Uses top + height of main crop box
                                left: cropOverlayStyleProps.left, // Uses the left of the main crop box
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'sw-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                            
                            {/* Bottom-right corner */}
                            <Box
                              onMouseDown={(e) => handleCornerMouseDown('br', e)}
                              sx={{
                                position: 'absolute',
                                top: `calc(${cropOverlayStyleProps.top} + ${cropOverlayStyleProps.height})`, // Uses top + height of main crop box
                                left: `calc(${cropOverlayStyleProps.left} + ${cropOverlayStyleProps.width})`, // Uses left + width of main crop box
                                width: '12px',
                                height: '12px',
                                backgroundColor: isLetterboxed ? '#ff4444' : isPillarboxed ? '#ff9800' : isPillarboxed43 ? '#4caf50' : isWideFile ? '#ff4444' : '#ff4444',
                                border: '2px solid white',
                                borderRadius: '50%',
                                cursor: 'se-resize',
                                pointerEvents: 'auto',
                                transform: 'translate(-50%, -50%)',
                                zIndex: 10,
                                '&:hover': {
                                  backgroundColor: isLetterboxed ? '#ff6666' : isPillarboxed ? '#ffb74d' : isPillarboxed43 ? '#66bb6a' : isWideFile ? '#f57c00' : '#f57c00',
                                  transform: 'translate(-50%, -50%) scale(1.2)'
                                }
                              }}
                            />
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Video Metadata */}
              {videoMetadata && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Video Information:</Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    <Chip label={`${videoMetadata.width}x${videoMetadata.height}`} size="small" />
                    <Chip label={`${videoMetadata.duration.toFixed(1)}s`} size="small" />
                    <Chip label={`${videoMetadata.aspectRatio.toFixed(2)}:1`} size="small" />
                  </Box>
                </Box>
              )}

              {/* AI Crop Detection */}
              {isVideoLoaded && (
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="outlined"
                    startIcon={isAIDetecting ? <CircularProgress size={20} /> : <SmartToyIcon />}
                    onClick={detectOptimalCrop}
                    disabled={isAIDetecting || isProcessing}
                    sx={{ mr: 2 }}
                  >
                    {isAIDetecting ? 'Analyzing...' : (is43Video ? 'Regenerate Preview' : 'AI Detect Crop')}
                  </Button>
                  {!is43Video && videoMetadata && (() => {
                    const targetAspect = 16 / 9;
                    const videoAspect = videoMetadata.width / videoMetadata.height;
                    const isWithin5Percent = Math.abs(videoAspect - targetAspect) / targetAspect <= 0.05;
                    return isWithin5Percent;
                  })() && (
                    <Button
                      variant="outlined"
                      onClick={setFullFrameCrop}
                      disabled={isProcessing}
                      sx={{ mr: 2 }}
                    >
                      Full Frame
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={() => setShowCropOverlay(!showCropOverlay)}
                    disabled={isProcessing}
                  >
                    {showCropOverlay ? (is43Video ? 'Hide Preview' : 'Hide Crop') : (is43Video ? 'Show Preview' : 'Show Crop')}
                  </Button>
                </Box>
              )}

              {/* Crop Controls or Preview Info */}
              {showCropOverlay && videoMetadata && (
                <Box sx={{ mt: 3 }}>
                  {is43Video ? (
                    // Show info for file-level 4:3 videos
                    <Box>
                      <Typography variant="subtitle2" gutterBottom sx={{ color: '#4caf50', fontWeight: 'bold' }}>
                        4:3 Video Detected - Blurred Background Style:
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Your video will be processed with the original 4:3 content centered vertically and a beautifully blurred background filling the 16:9 frame. No cropping will be applied to preserve all your content.
                      </Typography>
                    </Box>
                  ) : !isPillarboxed43 && (
                    // Show crop controls for regular 16:9 videos
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>Crop Settings (16:9 aspect ratio):</Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2 }}>
                        <TextField
                          label="X Position"
                          type="number"
                          value={Math.round(cropArea.x)}
                          onChange={(e) => handleCropChange('x', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: videoMetadata.width }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Y Position"
                          type="number"
                          value={Math.round(cropArea.y)}
                          onChange={(e) => handleCropChange('y', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: videoMetadata.height }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Width"
                          type="number"
                          value={Math.round(cropArea.width)}
                          onChange={(e) => handleCropChange('width', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 1, max: videoMetadata.width }}
                          size="small"
                          fullWidth
                        />
                        <TextField
                          label="Height"
                          type="number"
                          value={Math.round(cropArea.height)}
                          onChange={(e) => handleCropChange('height', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 1, max: videoMetadata.height }}
                          size="small"
                          fullWidth
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Processing Section */}
        {isVideoLoaded && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Process Video
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {is43Video || isPillarboxed43
                  ? "This will process your video with blurred background styling, scale to 1920x1080, normalize audio to ATSC A/85 standards, and upload to S3."
                  : "This will crop the video to 16:9 aspect ratio, scale to 1920x1080, normalize audio to ATSC A/85 standards, and upload to S3."
                }
              </Typography>

              {!isProcessing ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={processVideo}
                  disabled={!showCropOverlay}
                  sx={{ mr: 2 }}
                >
                  Process & Upload Video
                </Button>
              ) : (
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {processingProgress === -1 ? 'Generating vertical video (via AutoCrop)...' : processingStep}
                    </Typography>
                    {processingProgress >= 0 && (
                      <Typography variant="body2">
                        {processingPhase === 'horizontal' && '📹 Horizontal: '}
                        {processingPhase === 'vertical' && '📱 Vertical: '}
                        {processingProgress}%
                      </Typography>
                    )}
                  </Box>
                  <LinearProgress
                    variant={processingProgress === -1 ? 'indeterminate' : 'determinate'}
                    value={processingProgress === -1 ? undefined : processingProgress}
                    sx={{
                      backgroundColor: processingPhase === 'vertical' || processingProgress === -1 ? '#e3f2fd' : undefined,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: processingPhase === 'vertical' || processingProgress === -1 ? '#2196f3' : undefined
                      }
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {/* Video Results Section */}
        {(processedVideoUrl || verticalVideoUrl) && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Video Results
              </Typography>
              
              {/* Processed Video Result */}
              {processedVideoUrl && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    Processed Video Ready
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => window.open(processedVideoUrl, '_blank')}
                      sx={{ flexShrink: 0 }}
                    >
                      View Video
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {processedVideoUrl.substring(processedVideoUrl.lastIndexOf('/') + 1)}
                    </Typography>
                  </Box>
                </Box>
              )}

              {/* Vertical Video Result */}
              {verticalVideoUrl ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ color: '#4caf50' }} />
                    Vertical Video Ready
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => window.open(verticalVideoUrl, '_blank')}
                      sx={{ flexShrink: 0 }}
                    >
                      View Vertical Video
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {verticalVideoUrl.substring(verticalVideoUrl.lastIndexOf('/') + 1)}
                    </Typography>
                  </Box>
                </Box>
              ) : showRetryVertical && (
                <Box sx={{ mb: 2 }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Vertical video generation failed. Would you like to try again?
                  </Alert>
                  <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={retryVerticalGeneration}
                    disabled={isProcessing}
                  >
                    Retry Vertical Generation
                  </Button>
                </Box>
              )}

              {/* Reset Button */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={isProcessing}
                >
                  Process Another Video
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>

      {/* Hidden canvas for AI crop detection */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
      />
    </Box>
  );
}; 